import React, { useEffect, useMemo, useState, useRef } from "react";
import client from "../lib/api";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { downloadManual } from "../lib/manuals";
import { ImportButton } from "../components/DataImport";
import { AttachmentsButton } from "../components/Attachments";
import { translate as t, tp, APP_FULL, APP_VERSIONS } from "../i18n";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Download, Search, Eye, EyeOff, Trash2, GitBranch, LayoutGrid, Network, TreePine, Settings2, Pencil, GitMerge, Printer, FileSpreadsheet, ImagePlus, RotateCw, MoveHorizontal, MoveVertical, Share2, RefreshCw, Copy, Mail, MessageCircle, QrCode, Inbox, Check, X, BarChart3, CheckSquare, Square, Users, FolderPlus, FolderInput, HelpCircle, Link2, ChevronDown, ChevronRight, Coins, Loader2, Upload } from "lucide-react";
import { printFamilyTree, PAPER } from "../lib/printFamilyTree";
import FamilyDiagram from "../components/FamilyDiagram";
import RankBoard from "../components/RankBoard";
import RateCard from "../components/RateCard";
import { orderGenericGroups, orderGenericFlat } from "../lib/orgRank";
import AutomationCard from "../components/AutomationCard";
import { LiveBackupIndicator } from "../components/LiveBackup";
import { PoolAlertFlasher } from "../components/PoolAlertFlasher";
import QuickTour, { replayTour } from "../components/QuickTour";
import NearestPanel from "../components/NearestPanel";

const BRANCH_COLORS = [
  { hex: "#0F62FE", name: "Blue" }, { hex: "#24A148", name: "Green" }, { hex: "#DA1E28", name: "Red" },
  { hex: "#8A3FFC", name: "Purple" }, { hex: "#FF832B", name: "Orange" }, { hex: "#007D79", name: "Teal" },
  { hex: "#D02670", name: "Magenta" }, { hex: "#9F1853", name: "Crimson" },
];

// Attribute field definitions per tree type. Labels are defaults; per-tree overrides live on the tree.
const FAMILY_FIELDS = [
  { key: "maiden", label: "Maiden Name", max: 15 },
  { key: "spouse_first", label: "Spouse First", max: 15 },
  { key: "spouse_middle", label: "Spouse Middle", max: 15 },
  { key: "spouse_last", label: "Spouse Last", max: 15 },
  { key: "live_born", label: "Live Born Issue", type: "yesno" },
  { key: "mother_first", label: "Mother First", max: 15 },
  { key: "mother_middle", label: "Mother Middle", max: 15 },
  { key: "mother_last", label: "Mother Last", max: 15 },
  { key: "father_first", label: "Father First", max: 15 },
  { key: "father_middle", label: "Father Middle", max: 15 },
  { key: "father_last", label: "Father Last", max: 15 },
  { key: "fx1", label: "Wedding date", type: "date", max: 40 },
  { key: "fx2", label: "", max: 40 },
  { key: "fx3", label: "", max: 40 },
];
const GENERIC_FIELDS = [
  { key: "start_date", label: "Start date", type: "date", section: "Occupation" },
  { key: "end_date", label: "Dismissal / End date", type: "date", section: "Occupation" },
  { key: "title", label: "Title or Role", max: 40, section: "Occupation" },
  { key: "active", label: "Active or Inactive", type: "active", section: "Occupation" },
  { key: "location", label: "Location", max: 40, section: "Occupation" },
  { key: "principal_group", label: "Principal Group", type: "treegroup", section: "Occupation" },
  { key: "secondary_group", label: "Secondary Group", type: "treegroup", section: "Occupation" },
  { key: "gx3", label: "Group rank (A–Z)", type: "rank", section: "Occupation" },
  { key: "dept", label: "Department or group name (for layout & presentation)", type: "deptname", section: "Occupation", full: true },
  { key: "salary_grade", label: "Salary Grade", type: "grade", section: "Compensation", noCard: true },
  { key: "rate_of_pay", label: "Rate of Pay", type: "options", options: ["Weekly", "Biweekly", "Monthly", "26× per annum"], section: "Compensation" },
  { key: "manner_of_pay", label: "Manner of Pay", type: "options", options: ["Cheque", "Deposit", "Cash", "Wire"], section: "Compensation", noCard: true },
  { key: "vacation_pay", label: "Vacation pay", type: "money_mode", section: "Compensation", noCard: true },
  { key: "last_raise", label: "Last raise", type: "date", section: "Compensation" },
  { key: "admin_addition", label: "Admin addition", type: "money_mode", section: "Compensation", noCard: true },
  { key: "pay_hourly", label: "Hourly payee", type: "yntoggle", hint: "Adds this person to the hourly-payee group (surfaces in hourly searches).", section: "Compensation", full: true, noCard: true },
  { key: "enable_logs", label: "Enable work logs", type: "yntoggle", hint: "Lets the direct supervisor clock this person in/out for time tracking.", section: "Compensation", full: true, noCard: true },
  { key: "prompt_rate_card", label: "Prompt for rate card", type: "yntoggle", hint: "Prompts admin to pre-populate and the supervisor to submit a rate card for the period.", section: "Compensation", full: true, noCard: true },
  { key: "gx1", label: "First Designation", type: "designation", baseOpts: ["Prospect", "Agent", "Item", "Node", "Topic", "Element", "Resident", "Specimen"], optKeys: ["gx1_opt1", "gx1_opt2"], section: "IDEA" },
  { key: "gx4", label: "Second Designation", type: "designation", baseOpts: ["Home", "Away", "Project", "Task", "Investigation", "Contingency", "Feeder", "Elective", "Exigent", "Designee", "Student"], optKeys: ["gx4_opt1", "gx4_opt2"], section: "IDEA" },
  { key: "desig_rank", label: "Designation priority", type: "masterpair", cat: "desig", section: "IDEA", full: true },
  { key: "gx2", label: "Exigency", type: "exigency", section: "IDEA" },
  { key: "exig_rank", label: "Exigency priority", type: "masterpair", cat: "exig", section: "IDEA", full: true },
];
const SECTION_LABELS = { IDEA: "Indexing for Designation and Exigency Analysis (IDEA)" };
const fieldsFor = (type) => (type === "generic" ? GENERIC_FIELDS : FAMILY_FIELDS);
const emptyPerson = { first: "", middle: "", last: "", dob: "", dod: "", branch: "Main", color: "#0F62FE", parent_id: "", parent_id2: "", spouse_id: "", spouse_ids: [], former_spouse_ids: [], sibling_ids: [], relations: [], descend_from: "both", attrs: {}, photo: "", photo_rot: 0 };
const nameOf = (p) => [p?.first, p?.middle, p?.last].filter(Boolean).join(" ") || "Unnamed";
const gnum = (x) => Number(x) || 0;
const fmtMoney = (x) => "$" + gnum(x).toLocaleString(undefined, { maximumFractionDigits: 2 });
const DEFAULT_GRADES = Array.from({ length: 75 }, (_, i) => ({ grade: i + 1, amount: 0, label: "", rate_type: "salary", pay_frequency: "annual" }));
const PAY_FREQ = [
  { key: "annual", label: "Annual", short: "/yr", div: 1 },
  { key: "monthly", label: "Monthly", short: "/mo", div: 12 },
  { key: "semimonthly", label: "Semi-monthly", short: "/smo", div: 24 },
  { key: "biweekly", label: "Biweekly", short: "/2wk", div: 26 },
  { key: "weekly", label: "Weekly", short: "/wk", div: 52 },
];
const FREQ_DIV = Object.fromEntries(PAY_FREQ.map((f) => [f.key, f.div]));
const FREQ_SHORT = Object.fromEntries(PAY_FREQ.map((f) => [f.key, f.short]));
const RATE_TYPES = [
  { key: "salary", label: "Salary" },
  { key: "hourly", label: "Hourly" },
  { key: "pay_once", label: "Pay once" },
  { key: "month_day_1", label: "Month day 1" },
];
const RATE_KEYS = new Set(RATE_TYPES.map((r) => r.key));
// G-table pay-type colours: salary = default/black, hourly = purple, pay once = red, month day 1 = blue. Green reserved.
const RATE_COLOR = { hourly: "#8A3FFC", pay_once: "#DA1E28", month_day_1: "#0F62FE" };
const weekday = (d) => { try { return d ? new Date(d).toLocaleDateString(undefined, { weekday: "long" }) : ""; } catch { return ""; } };

// Resize + compress an uploaded image to a portable base64 data URL (travels with backups/exports).
function readAndResize(file, cb) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 420;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      cb(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

export default function FamilyTree() {
  const { lang, user } = useApp();
  const isDemo = !!(user && user.demo);
  const [people, setPeople] = useState([]);
  const [trees, setTrees] = useState([]);
  const [activeTree, setActiveTree] = useState("");
  const [open, setOpen] = useState(false);
  const [rateCardOpen, setRateCardOpen] = useState(false);
  const [hourlyOpen, setHourlyOpen] = useState(false);
  const [hourlyQ, setHourlyQ] = useState("");
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [payrollStart, setPayrollStart] = useState("");
  const [payrollEnd, setPayrollEnd] = useState("");
  const [payrollBusy, setPayrollBusy] = useState(false);
  const exportPayrollCsv = async () => {
    setPayrollBusy(true);
    try {
      const params = {};
      if (payrollStart) params.start = payrollStart;
      if (payrollEnd) params.end = payrollEnd;
      const res = await client.get("/payroll/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "payroll_processed.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
    setPayrollBusy(false);
    setPayrollOpen(false);
  };
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyPerson);
  const [q, setQ] = useState("");
  const [showDob, setShowDob] = useState(true);
  const [showDod, setShowDod] = useState(true);
  const [focus, setFocus] = useState(null);
  const [view, setView] = useState("cards");
  const [orientation, setOrientation] = useState("portrait");
  const [treeModal, setTreeModal] = useState(false);
  const [newTree, setNewTree] = useState({ name: "", type: "family" });
  const [labelModal, setLabelModal] = useState(false);
  const [labelDraft, setLabelDraft] = useState({});
  const [delModal, setDelModal] = useState(false);
  const [disp, setDisp] = useState({ mode: "reassign", target_tree_id: "" });
  const [orphans, setOrphans] = useState(0);
  const [orphanTarget, setOrphanTarget] = useState("");
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeTarget, setMergeTarget] = useState("");
  const [printModal, setPrintModal] = useState(false);
  const [pOpts, setPOpts] = useState({ mode: "diagram", layout: "single", cols: 2, rows: 2, paperKey: "letter", orientation: "portrait", branding: true });
  const [printBranches, setPrintBranches] = useState(null);
  const [shareModal, setShareModal] = useState(false);
  const [share, setShare] = useState({ enabled: false, token: null, show_dates: true, show_photos: true, notify: false, notify_mode: "first", stats: {} });
  const [copied, setCopied] = useState(false);
  const [suggModal, setSuggModal] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [groupModal, setGroupModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [moveModal, setMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [listModal, setListModal] = useState(false);
  const [listTitle, setListTitle] = useState("");
  const [bulkDelModal, setBulkDelModal] = useState(false);
  const [groupsModal, setGroupsModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});

  const shareUrl = share.token ? `${window.location.origin}/share/${share.token}` : "";
  const loadShare = (id) => client.get(`/trees/${id}/share`).then((r) => setShare(r.data)).catch(() => {});
  const refreshSuggestions = (id = activeTree) => id && client.get(`/trees/${id}/suggestions`).then((r) => setSuggestions(r.data || [])).catch(() => {});
  const pending = suggestions.filter((s) => s.status === "pending");
  const openShare = async () => { await loadShare(activeTree); setShareModal(true); };
  const saveShare = async (patch = {}) => {
    const next = { enabled: true, show_dates: share.show_dates, show_photos: share.show_photos, notify: share.notify, notify_mode: share.notify_mode, ...patch };
    const { data } = await client.post(`/trees/${activeTree}/share`, next);
    setShare(data);
  };
  const regenerateShare = async () => { const { data } = await client.post(`/trees/${activeTree}/share/regenerate`); setShare(data); };
  const revokeShare = async () => { const { data } = await client.post(`/trees/${activeTree}/share/revoke`); setShare({ ...data, show_dates: share.show_dates, show_photos: share.show_photos }); };
  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      } catch { /* clipboard unavailable */ }
    }
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  const emailLink = () => {
    const name = tree?.name || "our family tree";
    const subject = encodeURIComponent(`View "${name}"`);
    const body = encodeURIComponent(
      `Hi,\n\nHere is a read-only link to view "${name}". No account is needed \u2014 just open it in a browser:\n\n${shareUrl}\n\nYou can browse the tree and, if you like, suggest an addition (I approve every suggestion before it appears).\n`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const shareMessage = () => {
    const name = tree?.name || "our family tree";
    return `View "${name}" (read-only, no account needed): ${shareUrl}`;
  };
  const whatsappLink = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage())}`, "_blank", "noopener,noreferrer");
  };
  const nativeShare = async () => {
    const name = tree?.name || "our family tree";
    try {
      await navigator.share({ title: `View "${name}"`, text: shareMessage(), url: shareUrl });
    } catch { /* user cancelled or unsupported */ }
  };
  const qrRef = useRef(null);
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.download = `${(tree?.name || "family-tree").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-qr.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };
  const openSuggestions = async () => { await refreshSuggestions(); setSuggModal(true); };
  const acceptSug = async (id) => { await client.post(`/suggestions/${id}/accept`); await refreshSuggestions(); loadPeople(); };
  const dismissSug = async (id) => { await client.post(`/suggestions/${id}/dismiss`); refreshSuggestions(); };

  const [ideaFilter, setIdeaFilter] = useState(null); // active IDEA highlight from RankBoard; scopes CSV/Print when set
  const ideaFiltered = (arr) => (ideaFilter ? arr.filter((p) => (p.attrs?.[ideaFilter.key] ?? "").toString().trim() === String(ideaFilter.value)) : arr);
  const exportCsv = async () => {
    try {
      let qs = activeTree ? `?tree_id=${activeTree}` : "";
      if (treeType === "generic" && activeTree) {
        const ordered = orderGenericFlat(ideaFiltered(inTree), { dir: tree?.rank_dir || "asc", rankBy: tree?.rank_people_by || "salary_grade", rawAge, applyIdea: tree?.rank_idea_mode !== "override" });
        const ids = ordered.map((p) => p.id).join(",");
        qs = `?tree_id=${activeTree}&ids=${encodeURIComponent(ids)}`;
      }
      const r = await client.get(`/export/persons.csv${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      const catLabel = { gx1: "First-Designation", gx4: "Second-Designation", gx2: "Exigency" };
      const filterTag = ideaFilter ? `_${catLabel[ideaFilter.key] || "Filter"}_${String(ideaFilter.value)}` : "";
      const clean = (s) => s.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
      a.href = url; a.download = `${clean(tree?.name || "family_tree")}${clean(filterTag)}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };
  const exportJson = async () => {
    try {
      const qs = activeTree ? `?tree_id=${activeTree}` : "";
      const r = await client.get(`/export/persons.json${qs}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      const a = document.createElement("a");
      const clean = (s) => s.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
      a.href = url; a.download = `${clean(tree?.name || "family_tree")}.json`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
  };
  const [gedBusy, setGedBusy] = useState(false);
  const exportGedcom = async () => {
    if (!activeTree) return;
    try {
      const r = await client.get(`/gedcom/export/${activeTree}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/plain" }));
      const a = document.createElement("a");
      const clean = (s) => s.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
      a.href = url; a.download = `${clean(tree?.name || "family_tree")}.ged`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export GEDCOM."); }
  };
  const importGedcom = async (e, mode = "new") => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGedBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (mode === "merge" && activeTree) fd.append("tree_id", activeTree);
      const { data } = await client.post(`/gedcom/import`, fd);
      if (data.mode === "merge") toast.success(`Merged into "${data.tree_name}": ${data.created} added, ${data.merged} matched existing.`);
      else toast.success(`Imported ${data.created} people into "${data.tree_name}".`);
      setTimeout(() => window.location.reload(), 1400);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not import GEDCOM."); setGedBusy(false); }
    e.target.value = "";
  };

  const doPrint = () => {
    const sel = printBranches;
    if (treeType === "generic") {
      const groups = orderGenericGroups(ideaFiltered(inTree), { dir: tree?.rank_dir || "asc", rankBy: tree?.rank_people_by || "salary_grade", rawAge, applyIdea: tree?.rank_idea_mode !== "override" });
      printFamilyTree({ tree, people: groups.flatMap((g) => g.members), groups, axis: orientation, ...pOpts });
      setPrintModal(false);
      return;
    }
    const printPeople = Array.isArray(sel) ? inTree.filter((p) => sel.includes(p.branch || "Main")) : inTree;
    printFamilyTree({ tree, people: printPeople, axis: orientation, ...pOpts });
    setPrintModal(false);
  };
  const openPrint = () => {
    const brs = [...new Set(inTree.map((p) => p.branch || "Main"))].sort();
    setPrintBranches(brs);
    setPrintModal(true);
  };

  const loadOrphans = () => client.get("/trees/orphans").then((r) => setOrphans(r.data.count || 0)).catch(() => {});

  const loadPeople = () => client.get("/persons").then((r) => setPeople(r.data.map((p) => ({ id: p.id, ...(p.data || p) })))).catch(() => {});
  const loadTrees = () => client.get("/trees").then((r) => {
    const ts = r.data.map((x) => ({ id: x.id, ...(x.data || x) }));
    setTrees(ts);
    setActiveTree((cur) => cur || (ts[0] ? ts[0].id : ""));
  }).catch(() => {});
  useEffect(() => { loadPeople(); loadTrees(); loadOrphans(); }, []);
  useEffect(() => { if (activeTree) { refreshSuggestions(activeTree); setShare({ enabled: false, token: null, show_dates: true, show_photos: true, notify: false, notify_mode: "first", stats: {} }); } }, [activeTree]);

  const tree = trees.find((x) => x.id === activeTree) || null;
  const treeType = tree?.type || "family";
  const fields = fieldsFor(treeType);
  const labelFor = (f) => (tree?.labels?.[f.key]) || f.label || `Custom field (${f.key})`;

  const createTree = async () => {
    const name = (newTree.name || "").slice(0, 88).trim();
    if (!name) return;
    const { data } = await client.post("/trees", { data: { name, type: newTree.type, labels: {} } });
    setTreeModal(false); setNewTree({ name: "", type: "family" });
    await loadTrees(); setActiveTree(data.id);
  };
  const otherTrees = trees.filter((x) => x.id !== activeTree);
  const openDelTree = () => {
    if (!tree) return;
    setDisp({ mode: otherTrees.length ? "reassign" : "unassign", target_tree_id: otherTrees[0]?.id || "" });
    setDelModal(true);
  };
  const confirmDelTree = async () => {
    const payload = { mode: disp.mode, target_tree_id: disp.mode === "reassign" ? disp.target_tree_id : null };
    await client.post(`/trees/${tree.id}/dispose`, payload);
    setDelModal(false); setActiveTree(""); setFocus(null);
    await loadTrees(); loadPeople(); loadOrphans();
  };
  const cleanupOrphans = async (mode) => {
    if (mode === "reassign" && !orphanTarget) return;
    await client.post("/trees/orphans/dispose", { mode, target_tree_id: mode === "reassign" ? orphanTarget : null });
    loadPeople(); loadOrphans();
  };
  const openMerge = () => { setMergeTarget(otherTrees[0]?.id || ""); setMergeModal(true); };
  const confirmMerge = async () => {
    if (!mergeTarget) return;
    const dest = mergeTarget;
    await client.post(`/trees/${tree.id}/dispose`, { mode: "reassign", target_tree_id: dest });
    setMergeModal(false); setActiveTree(dest); setFocus(null);
    await loadTrees(); loadPeople(); loadOrphans();
  };
  const openLabels = () => { setLabelDraft({ ...(tree?.labels || {}) }); setLabelModal(true); };
  const saveLabels = async () => {
    await client.put(`/trees/${tree.id}`, { data: { ...tree, labels: labelDraft } });
    setLabelModal(false); loadTrees();
  };
  const saveOneLabel = async (key, name) => {
    if (!tree || (tree.labels?.[key] || "") === name) return;
    await client.put(`/trees/${tree.id}`, { data: { ...tree, labels: { ...(tree.labels || {}), [key]: name } } });
    loadTrees();
  };

  const openAdd = () => { setEditId(null); setForm({ ...emptyPerson, attrs: {} }); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ first: p.first || "", middle: p.middle || "", last: p.last || "", dob: p.dob || "", dod: p.dod || "",
      branch: p.branch || "Main", color: p.color || "#0F62FE", parent_id: p.parent_id || "", parent_id2: p.parent_id2 || "",
      spouse_id: p.spouse_id || "", spouse_ids: p.spouse_ids || (p.spouse_id ? [p.spouse_id] : []), former_spouse_ids: p.former_spouse_ids || [], sibling_ids: p.sibling_ids || [], relations: p.relations || [], descend_from: p.descend_from || "both",
      attrs: { ...(p.attrs || {}) },
      photo: p.photo || "", photo_rot: p.photo_rot || 0 });
    setOpen(true);
  };
  // "Sibling to": create a structural sibling link (works even with NO parents — e.g. patriarch brothers).
  // Does NOT copy branch/colour (siblings can head separate branches). Shares parents only if the sibling has them.
  const addSibling = (sibId) => {
    if (!sibId) return;
    const s = people.find((p) => p.id === sibId);
    if (!s) return;
    setForm((f) => ({
      ...f,
      sibling_ids: [...new Set([...(f.sibling_ids || []), sibId])],
      parent_id: f.parent_id || s.parent_id || "",
      parent_id2: f.parent_id2 || s.parent_id2 || "",
    }));
  };
  const removeSibling = (sid) => setForm((f) => ({ ...f, sibling_ids: (f.sibling_ids || []).filter((x) => x !== sid) }));
  // Other (non-hierarchical) relationships: cousin / common ancestor, referencing another person on the tree.
  const [relType, setRelType] = useState("cousin");
  const [relPerson, setRelPerson] = useState("");
  const addRelation = () => {
    if (!relPerson) return;
    setForm((f) => {
      const cur = f.relations || [];
      if (cur.some((r) => r.type === relType && r.id === relPerson)) return f;
      return { ...f, relations: [...cur, { type: relType, id: relPerson }] };
    });
    setRelPerson("");
  };
  const removeRelation = (i) => setForm((f) => ({ ...f, relations: (f.relations || []).filter((_, idx) => idx !== i) }));
  // "Child of": auto-fill the second parent from the chosen parent's (first) spouse
  const pickParent = (pid) => {
    const p = people.find((x) => x.id === pid);
    const sp = p ? (p.spouse_ids?.[0] || p.spouse_id || "") : "";
    setForm((f) => ({ ...f, parent_id: pid, parent_id2: sp || f.parent_id2 }));
  };
  const nameById = (id) => { const p = people.find((x) => x.id === id); return p ? nameOf(p) : ""; };
  const groupLabel = (v) => { if (!v) return ""; for (const g of groupOptions) { const m = g.values.find((o) => o.v === v); if (m) return m.label; } return v; };
  const addSpouse = (sid) => {
    if (!sid) return;
    setForm((f) => {
      const cur = f.spouse_ids || [];
      if (cur.includes(sid) || cur.length >= 9) return f;
      return { ...f, spouse_ids: [...cur, sid], former_spouse_ids: (f.former_spouse_ids || []).filter((x) => x !== sid) };
    });
  };
  const removeSpouse = (sid) => setForm((f) => ({ ...f, spouse_ids: (f.spouse_ids || []).filter((x) => x !== sid) }));
  const addFormerSpouse = (sid) => {
    if (!sid) return;
    setForm((f) => {
      const cur = f.former_spouse_ids || [];
      if (cur.includes(sid)) return f;
      return { ...f, former_spouse_ids: [...cur, sid], spouse_ids: (f.spouse_ids || []).filter((x) => x !== sid) };
    });
  };
  const removeFormerSpouse = (sid) => setForm((f) => ({ ...f, former_spouse_ids: (f.former_spouse_ids || []).filter((x) => x !== sid) }));
  // Choosing a new colour offers to spin it off into a new (sibling) branch.
  const [branchPrompt, setBranchPrompt] = useState({ open: false, color: "", name: "" });
  const pickColor = (hex) => {
    if (hex === form.color) return;
    setBranchPrompt({ open: true, color: hex, name: "" });
  };
  const applyColorOnly = () => { setForm((f) => ({ ...f, color: branchPrompt.color })); setBranchPrompt({ open: false, color: "", name: "" }); };
  const applyNewBranch = () => {
    const name = (branchPrompt.name || "").slice(0, 40).trim();
    if (!name) return;
    setForm((f) => ({ ...f, color: branchPrompt.color, branch: name }));
    setBranchPrompt({ open: false, color: "", name: "" });
  };
  // Re-colour every member of the current person's named branch (in this tree) in one action.
  const applyToBranch = async () => {
    const hex = branchPrompt.color;
    const br = (form.branch || "").trim();
    const targets = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "").trim() === br);
    await Promise.all(targets.map((p) => client.put(`/persons/${p.id}`, { data: { color: hex } }).catch(() => {})));
    setForm((f) => ({ ...f, color: hex }));
    setBranchPrompt({ open: false, color: "", name: "" });
    loadPeople();
  };
  // Rename a whole branch across every tagged member (in this tree) in one action.
  const [renamePrompt, setRenamePrompt] = useState({ open: false, from: "", name: "" });
  const [branchPanel, setBranchPanel] = useState(false);
  const recolourBranch = async (name, hex) => {
    const targets = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "Main").trim() === name);
    await Promise.all(targets.map((p) => client.put(`/persons/${p.id}`, { data: { color: hex } }).catch(() => {})));
    loadPeople();
  };
  // Merge a branch into another: reassign every member to the target branch name + colour.
  const mergeBranch = async (from, to) => {
    if (!to || to === from) return;
    const tgt = people.find((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "Main").trim() === to);
    const tgtColor = tgt?.color;
    const targets = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "Main").trim() === from);
    await Promise.all(targets.map((p) => client.put(`/persons/${p.id}`, { data: tgtColor ? { branch: to, color: tgtColor } : { branch: to } }).catch(() => {})));
    loadPeople();
  };
  // Dropdown sort order for people pickers (persisted).
  const [linkSort, setLinkSort] = useState(() => localStorage.getItem("familigi_link_sort") || "added");
  useEffect(() => { localStorage.setItem("familigi_link_sort", linkSort); }, [linkSort]);
  const [spouseSort, setSpouseSort] = useState("first"); // "first" = alpha by first name | "added" = descending by last added
  const [formerSpouseSort, setFormerSpouseSort] = useState("first");
  // Demographics / age-band counts.
  const DEFAULT_BANDS = [
    { label: "0–12", min: 0, max: 12 }, { label: "13–20", min: 13, max: 20 },
    { label: "21–39", min: 21, max: 39 }, { label: "40–64", min: 40, max: 64 },
    { label: "65+", min: 65, max: 200 }, { label: "", min: "", max: "" },
    { label: "", min: "", max: "" }, { label: "", min: "", max: "" },
  ];
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoLivingOnly, setDemoLivingOnly] = useState(false);
  const [demoMsg, setDemoMsg] = useState("");
  // Turn a (branch, age-band) cell into a reusable benefit/payee group — feeds cluster payment scheduling.
  const createBandGroup = async (branchKey, branchLabel, band, arr) => {
    const ids = arr.filter((p) => {
      if (demoLivingOnly && p.dod) return false;
      const age = ageOf(p);
      return age !== null && age >= Number(band.min) && age <= Number(band.max);
    }).map((p) => p.id);
    if (!ids.length) return;
    const label = band.label || `${band.min}-${band.max}`;
    const name = `${branchLabel} · ${label}${demoLivingOnly ? " · living" : ""}`.slice(0, 88);
    await client.post("/groups", { name, tree_id: activeTree, member_ids: ids,
      criteria: { type: "band", branch: branchKey, min: Number(band.min), max: Number(band.max), living_only: demoLivingOnly } });
    loadGroups();
    setDemoMsg(tp(`Created benefit group “${name}” — ${ids.length} member(s). Find it under “Groups”.`, lang));
  };
  // Refresh a band-derived group's membership from current data (birthdays/deaths change over time).
  const computeBandIds = (cr, treeId) => {
    const pool = people.filter((p) => (treeId ? p.tree_id === treeId : true));
    const scoped = cr.branch === "__ALL__" ? pool : pool.filter((p) => (p.branch || "Main").trim() === cr.branch);
    return scoped.filter((p) => {
      if (cr.living_only && p.dod) return false;
      const age = ageOf(p);
      return age !== null && age >= Number(cr.min) && age <= Number(cr.max);
    }).map((p) => p.id);
  };
  const resyncGroup = async (g) => {
    if (!g.criteria || g.criteria.type !== "band") return;
    await client.put(`/groups/${g.id}`, { member_ids: computeBandIds(g.criteria, g.tree_id), criteria: g.criteria });
    loadGroups();
  };
  const [resyncAllMsg, setResyncAllMsg] = useState("");
  const resyncAllGroups = async () => {
    const band = groups.filter((g) => g.criteria?.type === "band");
    if (!band.length) return;
    await Promise.all(band.map((g) => client.put(`/groups/${g.id}`, { member_ids: computeBandIds(g.criteria, g.tree_id), criteria: g.criteria }).catch(() => {})));
    loadGroups();
    setResyncAllMsg(tp(`Re-synced ${band.length} band group(s) from current data.`, lang));
  };
  const [ageBands, setAgeBands] = useState(DEFAULT_BANDS);
  useEffect(() => {
    const b = tree?.age_bands;
    setAgeBands(Array.isArray(b) && b.length ? [...b, ...DEFAULT_BANDS].slice(0, 8) : DEFAULT_BANDS);
  }, [tree?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveDemoBands = async () => { if (!tree) return; await client.put(`/trees/${tree.id}`, { data: { ...tree, age_bands: ageBands } }); await loadTrees(); };

  // Salary grades (central per-account 1–25 → amount table) for generic/org trees.
  const [salaryGrades, setSalaryGrades] = useState(DEFAULT_GRADES);
  const [gradesOpen, setGradesOpen] = useState(false);
  const [gradesDirty, setGradesDirty] = useState(false);
  const [gradesMsg, setGradesMsg] = useState("");
  const [gradeView, setGradeView] = useState("annual");       // global display lens
  const [popupRow, setPopupRow] = useState(null);             // row index showing the conversion popup
  const [popupSuppressed, setPopupSuppressed] = useState(() => localStorage.getItem("grade_popup_suppressed") === "1");
  const suppressGradePopups = () => { localStorage.setItem("grade_popup_suppressed", "1"); setPopupSuppressed(true); setPopupRow(null); };
  const flagGradePopup = (i) => { if (!popupSuppressed) setPopupRow(i); };
  useEffect(() => { client.get("/salary-grades").then((r) => { const saved = Array.isArray(r.data) ? r.data : []; const map = Object.fromEntries(saved.map((g) => [String(g.grade), g])); setSalaryGrades(DEFAULT_GRADES.map((d) => map[String(d.grade)] ? { ...d, ...map[String(d.grade)] } : d)); }).catch(() => setSalaryGrades(DEFAULT_GRADES)); }, []);
  const editGrade = (i, key, value) => { setSalaryGrades((a) => a.map((x, j) => j === i ? { ...x, [key]: value } : x)); setGradesDirty(true); setGradesMsg(""); };
  const saveGrades = async () => {
    try {
      const clean = salaryGrades.map((g) => ({ grade: g.grade, label: g.label || "", amount: Number(String(g.amount ?? "").replace(/[^0-9.\-]/g, "")) || 0, rate_type: RATE_KEYS.has(g.rate_type) ? g.rate_type : "salary", pay_frequency: (g.rate_type === "salary" && FREQ_DIV[g.pay_frequency]) ? g.pay_frequency : "annual" }));
      const r = await client.put("/salary-grades", { grades: clean });
      const saved = Array.isArray(r.data?.grades) ? r.data.grades : clean;
      const map = Object.fromEntries(saved.map((g) => [String(g.grade), g]));
      setSalaryGrades(DEFAULT_GRADES.map((d) => map[String(d.grade)] ? { ...d, ...map[String(d.grade)] } : d));
      const n = clean.filter((g) => g.amount).length;
      setGradesDirty(false); setGradesMsg(`Saved ✓ — ${n} grade${n === 1 ? "" : "s"} with an amount`);
    } catch (e) { setGradesMsg("Save failed — please try again."); }
  };
  const closeGrades = () => { if (gradesDirty && !window.confirm("You have unsaved salary-grade changes. Discard them?")) return; setGradesDirty(false); setGradesMsg(""); setGradesOpen(false); };
  const gradeAmount = (g) => { const row = salaryGrades.find((x) => String(x.grade) === String(g)); return row ? gnum(row.amount) : 0; };
  const gradeRate = (g) => { const row = salaryGrades.find((x) => String(x.grade) === String(g)); const rt = row && row.rate_type; return RATE_KEYS.has(rt) ? rt : "salary"; };
  const gradeFreq = (g) => { const row = salaryGrades.find((x) => String(x.grade) === String(g)); return (row && FREQ_DIV[row.pay_frequency]) ? row.pay_frequency : "annual"; };
  const gradeEquiv = (annual) => { const a = gnum(annual); if (!a) return ""; return `${fmtMoney(a / 12)}/mo · ${fmtMoney(a / 24)} semi · ${fmtMoney(a / 26)}/2wk · ${fmtMoney(a / 52)}/wk`; };
  const groupOptions = useMemo(() => trees.map((tr) => ({
    tree: tr.name,
    values: [{ v: tr.name, label: `${tr.name} (whole tree)` },
      ...[...new Set(people.filter((p) => p.tree_id === tr.id).map((p) => (p.branch || "").trim()).filter(Boolean))].sort().map((b) => ({ v: `${tr.name} / ${b}`, label: b }))],
  })), [trees, people]);
  const rawAge = (p) => {
    const m = /(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?/.exec(p.dob || "");
    if (!m) return null;
    const by = +m[1], bmo = m[2] ? +m[2] : 1, bd = m[3] ? +m[3] : 1;
    const end = p.dod ? new Date(p.dod) : new Date();
    let age = end.getFullYear() - by;
    if ((end.getMonth() + 1) < bmo || ((end.getMonth() + 1) === bmo && end.getDate() < bd)) age--;
    return age < 0 ? null : age;
  };
  const persistRank = async (r) => { if (!tree) return; try { await client.put(`/trees/${tree.id}`, { data: { ...tree, ...r } }); await loadTrees(); } catch (e) { /* noop */ } };
  const ageOf = (p) => {
    const m = /(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?/.exec(p.dob || "");
    if (!m) return null;
    const y = +m[1], mo = m[2] ? +m[2] : 1, d = m[3] ? +m[3] : 1;
    const t = new Date();
    let age = t.getFullYear() - y;
    if (t.getMonth() + 1 < mo || (t.getMonth() + 1 === mo && t.getDate() < d)) age -= 1;
    return age < 0 ? null : age;
  };
  const bandOf = (age) => {
    for (const b of ageBands) {
      if (b.min === "" || b.max === "") continue;
      if (age >= Number(b.min) && age <= Number(b.max)) return b;
    }
    return null;
  };
  const renameBranchAll = async () => {
    const from = (renamePrompt.from || "").trim();
    const to = (renamePrompt.name || "").slice(0, 40).trim();
    if (!to || to === from) { setRenamePrompt({ open: false, from: "", name: "" }); return; }
    const targets = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "").trim() === from);
    await Promise.all(targets.map((p) => client.put(`/persons/${p.id}`, { data: { branch: to } }).catch(() => {})));
    setForm((f) => ((f.branch || "").trim() === from ? { ...f, branch: to } : f));
    setRenamePrompt({ open: false, from: "", name: "" });
    loadPeople();
  };
  const save = async () => {
    const spids = [...new Set((form.spouse_ids || []).filter(Boolean).filter((x) => x !== editId))];
    const d = { ...form, spouse_ids: spids, spouse_id: spids[0] || "", tree_id: activeTree, tree_type: treeType };
    ["first", "middle", "last"].forEach((k) => (d[k] = (d[k] || "").slice(0, 15)));
    let id = editId;
    if (editId) await client.put(`/persons/${editId}`, { data: d });
    else { const res = await client.post("/persons", { data: d }); id = res.data?.id || res.data?.data?.id; }
    // Reconcile bi-directional spouse links (supports multiple spouses / polygamy)
    if (id) {
      const orig = editId ? people.find((p) => p.id === editId) : null;
      const prev = orig ? (orig.spouse_ids || (orig.spouse_id ? [orig.spouse_id] : [])) : [];
      const added = spids.filter((s) => !prev.includes(s));
      const removed = prev.filter((s) => !spids.includes(s));
      const relink = async (s, add) => {
        const sp = people.find((p) => p.id === s);
        if (!sp) return;
        const cur = new Set(sp.spouse_ids || (sp.spouse_id ? [sp.spouse_id] : []));
        add ? cur.add(id) : cur.delete(id);
        const arr = [...cur];
        await client.put(`/persons/${s}`, { data: { spouse_ids: arr, spouse_id: arr[0] || "" } }).catch(() => {});
      };
      for (const s of added) await relink(s, true);
      for (const s of removed) await relink(s, false);
      // Reconcile bi-directional sibling links
      const prevSib = orig ? (orig.sibling_ids || []) : [];
      const sibs = [...new Set((form.sibling_ids || []).filter(Boolean).filter((x) => x !== editId))];
      const addedS = sibs.filter((x) => !prevSib.includes(x));
      const removedS = prevSib.filter((x) => !sibs.includes(x));
      const relinkSib = async (x, add) => {
        const sp = people.find((p) => p.id === x);
        if (!sp) return;
        const cur = new Set(sp.sibling_ids || []);
        add ? cur.add(id) : cur.delete(id);
        await client.put(`/persons/${x}`, { data: { sibling_ids: [...cur] } }).catch(() => {});
      };
      for (const x of addedS) await relinkSib(x, true);
      for (const x of removedS) await relinkSib(x, false);
      // Reconcile bi-directional former-spouse links
      const prevFmr = orig ? (orig.former_spouse_ids || []) : [];
      const fmr = [...new Set((form.former_spouse_ids || []).filter(Boolean).filter((x) => x !== editId))];
      const relinkFmr = async (x, add) => {
        const sp = people.find((p) => p.id === x);
        if (!sp) return;
        const cur = new Set(sp.former_spouse_ids || []);
        add ? cur.add(id) : cur.delete(id);
        await client.put(`/persons/${x}`, { data: { former_spouse_ids: [...cur] } }).catch(() => {});
      };
      for (const x of fmr.filter((x) => !prevFmr.includes(x))) await relinkFmr(x, true);
      for (const x of prevFmr.filter((x) => !fmr.includes(x))) await relinkFmr(x, false);
      // NEAREST: propagate Wedding date (fx1) to all current spouses (bi-directional)
      const wed = d.attrs?.fx1 || "";
      for (const s of spids) {
        const sp = people.find((p) => p.id === s);
        if (!sp) continue;
        if ((sp.attrs?.fx1 || "") !== wed) {
          await client.put(`/persons/${s}`, { data: { attrs: { ...(sp.attrs || {}), fx1: wed } } }).catch(() => {});
        }
      }
    }
    setOpen(false); setEditId(null); setForm({ ...emptyPerson, attrs: {} }); loadPeople();
  };
  const del = async (id) => { await client.delete(`/persons/${id}`); loadPeople(); };

  // ---- Auto-link relationships: read typed Spouse/Mother/Father names → structural links ----
  const [autoLinkModal, setAutoLinkModal] = useState(false);
  const [autoLinkPlan, setAutoLinkPlan] = useState([]);
  const [autoLinkBusy, setAutoLinkBusy] = useState(false);
  const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const pFull = (p) => norm([p.first, p.middle, p.last].filter(Boolean).join(" "));
  const pFL = (p) => norm([p.first, p.last].filter(Boolean).join(" "));
  const aFull = (p, k) => norm([p.attrs?.[`${k}_first`], p.attrs?.[`${k}_middle`], p.attrs?.[`${k}_last`]].filter(Boolean).join(" "));
  const aFL = (p, k) => norm([p.attrs?.[`${k}_first`], p.attrs?.[`${k}_last`]].filter(Boolean).join(" "));
  const matchPerson = (full, fl, excludeId) => inTree.find((q) => q.id !== excludeId && (pFull(q) === full || (fl && pFL(q) === fl)));
  const buildAutoLinkPlan = () => {
    const plan = [];
    inTree.forEach((p) => {
      const item = { id: p.id, name: nameOf(p), spouse: null, father: null, mother: null };
      const sf = aFull(p, "spouse"); if (sf) { const q = matchPerson(sf, aFL(p, "spouse"), p.id); if (q && !(p.spouse_ids || (p.spouse_id ? [p.spouse_id] : [])).includes(q.id)) item.spouse = { id: q.id, name: nameOf(q) }; }
      const ff = aFull(p, "father"); if (ff) { const q = matchPerson(ff, aFL(p, "father"), p.id); if (q && p.parent_id !== q.id && p.parent_id2 !== q.id) item.father = { id: q.id, name: nameOf(q) }; }
      const mf = aFull(p, "mother"); if (mf) { const q = matchPerson(mf, aFL(p, "mother"), p.id); if (q && p.parent_id !== q.id && p.parent_id2 !== q.id) item.mother = { id: q.id, name: nameOf(q) }; }
      if (item.spouse || item.father || item.mother) plan.push(item);
    });
    return plan;
  };
  const openAutoLink = () => { setAutoLinkPlan(buildAutoLinkPlan()); setAutoLinkModal(true); };
  const applyAutoLink = async () => {
    setAutoLinkBusy(true);
    for (const item of autoLinkPlan) {
      const p = people.find((x) => x.id === item.id);
      if (!p) continue;
      const patch = {};
      if (item.father || item.mother) {
        let p1 = p.parent_id || "", p2 = p.parent_id2 || "";
        if (item.father && !p1 && item.father.id !== p2) p1 = item.father.id;
        if (item.mother && !p2 && item.mother.id !== p1) p2 = item.mother.id;
        patch.parent_id = p1; patch.parent_id2 = p2;
      }
      if (item.spouse) {
        const cur = new Set(p.spouse_ids || (p.spouse_id ? [p.spouse_id] : []));
        cur.add(item.spouse.id);
        patch.spouse_ids = [...cur]; patch.spouse_id = [...cur][0] || "";
        const sp = people.find((x) => x.id === item.spouse.id);
        const scur = new Set(sp?.spouse_ids || (sp?.spouse_id ? [sp.spouse_id] : []));
        scur.add(item.id);
        await client.put(`/persons/${item.spouse.id}`, { data: { spouse_ids: [...scur], spouse_id: [...scur][0] || "" } }).catch(() => {});
      }
      if (Object.keys(patch).length) await client.put(`/persons/${item.id}`, { data: patch }).catch(() => {});
    }
    setAutoLinkBusy(false); setAutoLinkModal(false); setAutoLinkPlan([]); loadPeople();
  };

  // ---- Selection & bulk actions ----
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const selectAllFiltered = () => setSelected(new Set(filtered.map((p) => p.id)));
  const selCount = selected.size;
  const selIds = () => [...selected];
  const editSelected = () => {
    if (selected.size !== 1) return;
    const p = people.find((x) => x.id === [...selected][0]);
    if (p) openEdit(p);
  };
  // Deselect anything not in the current filtered/tree view when the tree changes
  useEffect(() => { clearSel(); }, [activeTree]);

  const loadGroups = () => client.get("/groups").then((r) => setGroups(r.data || [])).catch(() => {});
  useEffect(() => { loadGroups(); }, []);

  const doBulkDelete = async () => {
    await client.post("/persons/bulk/delete", { ids: selIds() });
    setBulkDelModal(false); clearSel(); loadPeople(); loadOrphans();
  };
  const doCreateGroup = async () => {
    const name = (groupName || "").slice(0, 88).trim();
    if (!name) return;
    await client.post("/groups", { name, tree_id: activeTree, member_ids: selIds() });
    setGroupModal(false); setGroupName(""); loadGroups();
  };
  const openMove = () => { setMoveTarget(otherTrees[0]?.id || activeTree); setMoveModal(true); };
  const doMove = async () => {
    if (!moveTarget) return;
    await client.post("/persons/bulk/move", { ids: selIds(), tree_id: moveTarget });
    setMoveModal(false); clearSel(); await loadTrees(); loadPeople(); loadOrphans();
  };
  const doExportList = async () => {
    try {
      const title = (listTitle || "").trim();
      const params = new URLSearchParams();
      if (activeTree) params.set("tree_id", activeTree);
      params.set("ids", selIds().join(","));
      if (title) params.set("title", title);
      const r = await client.get(`/export/persons.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${(title || tree?.name || "selection").replace(/\s+/g, "_")}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ }
    setListModal(false); setListTitle("");
  };
  const deleteGroup = async (id) => { await client.delete(`/groups/${id}`); loadGroups(); };

  const setParent = async (sourceId, targetId) => {
    if (sourceId === targetId) return;
    setPeople((ps) => ps.map((p) => (p.id === targetId ? { ...p, parent_id: sourceId } : p)));
    await client.put(`/persons/${targetId}`, { data: { parent_id: sourceId } });
  };
  const moveNode = async (id, x, y) => {
    setPeople((ps) => ps.map((p) => (p.id === id ? { ...p, x, y } : p)));
    await client.put(`/persons/${id}`, { data: { x, y } });
  };

  // Clear any dragged positions in the active tree so the auto-layout takes over.
  const resetLayout = async () => {
    const list = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.x != null || p.y != null));
    if (!list.length) return;
    await Promise.all(list.map((p) => client.put(`/persons/${p.id}`, { data: { x: null, y: null } }).catch(() => {})));
    loadPeople();
  };

  const inTree = people.filter((p) => (activeTree ? p.tree_id === activeTree : true));
  const sortedInTree = useMemo(() => {
    const arr = [...inTree];
    if (linkSort === "first") arr.sort((a, b) => (a.first || "").localeCompare(b.first || ""));
    else if (linkSort === "middle") arr.sort((a, b) => (a.middle || "").localeCompare(b.middle || ""));
    else arr.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
    return arr;
  }, [inTree, linkSort]);
  const sortPeopleBy = (arr, mode) => {
    const a = [...arr];
    if (mode === "first") a.sort((x, y) => (x.first || "").localeCompare(y.first || ""));
    else a.sort((x, y) => String(y.created_at || "").localeCompare(String(x.created_at || "")));
    return a;
  };
  const SpouseSortToggle = ({ value, onChange, idBase }) => (
    <div className="mb-1 flex items-center gap-1.5 text-[11px]">
      <span className="opacity-60">{tp("Show", lang)}:</span>
      <div className="flex overflow-hidden rounded border border-black/15 dark:border-white/15">
        <button type="button" onClick={() => onChange("first")} data-testid={`${idBase}-first`}
          className={`px-2 py-0.5 ${value === "first" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{tp("Alpha by first name", lang)}</button>
        <button type="button" onClick={() => onChange("added")} data-testid={`${idBase}-added`}
          className={`px-2 py-0.5 ${value === "added" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{tp("Descending by last added", lang)}</button>
      </div>
    </div>
  );

  const deptOptions = useMemo(() => [...new Set(inTree.map((p) => (p.attrs?.dept || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [inTree]);

  const descendants = useMemo(() => {
    if (!focus) return new Set();
    const out = new Set(); const stack = [focus];
    while (stack.length) {
      const cur = stack.pop();
      inTree.forEach((p) => { if ((p.parent_id === cur || p.parent_id2 === cur) && !out.has(p.id)) { out.add(p.id); stack.push(p.id); } });
    }
    return out;
  }, [focus, inTree]);

  const searchText = (p) => {
    const parts = [p.first, p.middle, p.last];
    if (showDob) parts.push(p.dob, weekday(p.dob));
    if (showDod) parts.push(p.dod, weekday(p.dod));
    fields.forEach((f) => parts.push(p.attrs?.[f.key]));
    return parts.filter(Boolean).join(" ").toLowerCase();
  };
  const filtered = inTree.filter((p) => {
    const tokens = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return true;
    const hay = searchText(p);
    return tokens.every((tok) => hay.includes(tok));
  });

  const dateFieldMode = (key, value) => (form.attrs?.date_modes?.[key]) || (/^\d{4}$/.test(String(value || "")) ? "year" : "full");
  const renderDateInput = (key, label, value, onValue) => {
    const mode = dateFieldMode(key, value);
    const switchMode = (m) => {
      let nv = value || "";
      if (m === "year" && /^\d{4}-\d{2}-\d{2}$/.test(nv)) nv = nv.slice(0, 4);
      setForm((f) => ({ ...f, attrs: { ...f.attrs, date_modes: { ...(f.attrs?.date_modes || {}), [key]: m } } }));
      if (nv !== value) onValue(nv);
    };
    return (
      <div data-testid={`datefield-${key}`}>
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="block text-xs font-medium opacity-70">{label}</span>
          <div className="flex overflow-hidden rounded border border-black/15 text-[10px] dark:border-white/15">
            <button type="button" onClick={() => switchMode("full")} data-testid={`datemode-full-${key}`}
              className={`px-1.5 py-0.5 ${mode === "full" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{tp("Full date", lang)}</button>
            <button type="button" onClick={() => switchMode("year")} data-testid={`datemode-year-${key}`}
              className={`px-1.5 py-0.5 ${mode === "year" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{tp("Year only", lang)}</button>
          </div>
        </div>
        {mode === "year"
          ? <Input type="number" min="0" max="9999" placeholder="YYYY" value={value || ""} onChange={(e) => onValue(e.target.value.slice(0, 4))} data-testid={`p-${key}`} />
          : <Input type="date" value={value || ""} onChange={(e) => onValue(e.target.value)} data-testid={`p-${key}`} />}
      </div>
    );
  };
  const renderField = (f, val, onChange) => {
    if (f.type === "yesno") return <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">—</option><option value="Yes">Yes</option><option value="No">No</option></Select>;
    if (f.type === "active") return <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">—</option><option value="Active">{tp("Active", lang)}</option><option value="Inactive">{tp("Inactive", lang)}</option></Select>;
    if (f.type === "date") return renderDateInput(f.key, labelFor(f), val, (v) => onChange({ target: { value: v } }));
    if (f.type === "designation") {
      const [k1, k2] = f.optKeys || [];
      const extra = [tree?.labels?.[k1], tree?.labels?.[k2]].map((x) => (x || "").trim()).filter(Boolean);
      const opts = [...(f.baseOpts || []), ...extra];
      return (
        <div>
          <Select label={f.label} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}>
            <option value="">—</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </Select>
          <div className="mt-1 grid grid-cols-2 gap-1">
            <input defaultValue={tree?.labels?.[k1] || ""} placeholder={tp("＋ custom option", lang)} maxLength={40}
              onBlur={(e) => saveOneLabel(k1, e.target.value.trim())} data-testid={`p-${f.key}-opt1`}
              className="w-full rounded border border-dashed border-[var(--primary)]/50 bg-transparent px-1.5 py-1 text-[11px] placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
            <input defaultValue={tree?.labels?.[k2] || ""} placeholder={tp("＋ custom option", lang)} maxLength={40}
              onBlur={(e) => saveOneLabel(k2, e.target.value.trim())} data-testid={`p-${f.key}-opt2`}
              className="w-full rounded border border-dashed border-[var(--primary)]/50 bg-transparent px-1.5 py-1 text-[11px] placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
          </div>
        </div>
      );
    }
    if (f.type === "exigency") return <Select label={f.label} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">—</option>{Array.from({ length: 30 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}</Select>;
    if (f.type === "masterpair") {
      const cur = val || "";
      const set = (v) => onChange({ target: { value: cur === v ? "" : v } });
      return (
        <div className="flex items-center gap-4 py-1" data-testid={`p-${f.key}`}>
          <span className="text-xs font-medium opacity-70">{f.label}:</span>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs">
            <input type="checkbox" checked={cur === "master"} onChange={() => set("master")} data-testid={`p-${f.key}-master`} className="accent-[var(--primary)]" /> {tp("Master", lang)}
          </label>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs">
            <input type="checkbox" checked={cur === "submaster"} onChange={() => set("submaster")} data-testid={`p-${f.key}-submaster`} className="accent-[var(--primary)]" /> {tp("Submaster", lang)}
          </label>
        </div>
      );
    }
    if (f.type === "options") return <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</Select>;
    if (f.type === "num2") return <Input label={labelFor(f)} type="number" step="0.01" value={val ?? ""} onChange={onChange} data-testid={`p-${f.key}`} />;
    if (f.type === "money_mode") {
      const mode = form.attrs?.[`${f.key}_mode`] === "percent" ? "percent" : "figure";
      const setMode = (m) => setForm({ ...form, attrs: { ...form.attrs, [`${f.key}_mode`]: m } });
      return (
        <div>
          <label className="mb-1 block text-xs font-medium opacity-70">{labelFor(f)}</label>
          <div className="flex items-center gap-1">
            <span className="w-4 text-center text-xs opacity-60">{mode === "percent" ? "%" : "$"}</span>
            <input type="number" step="0.01" value={val ?? ""} onChange={onChange} data-testid={`p-${f.key}`}
              className="min-w-0 flex-1 rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15" />
            <div className="flex shrink-0 overflow-hidden rounded border border-black/15 text-[10px] dark:border-white/15">
              <button type="button" onClick={() => setMode("figure")} data-testid={`p-${f.key}-figure`}
                className={`px-1.5 py-1 ${mode === "figure" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>{tp("Figure", lang)}</button>
              <button type="button" onClick={() => setMode("percent")} data-testid={`p-${f.key}-percent`}
                className={`px-1.5 py-1 ${mode === "percent" ? "bg-[var(--primary)] text-white" : "opacity-70"}`}>%</button>
            </div>
          </div>
        </div>
      );
    }
    if (f.type === "yntoggle") {
      const set = (v) => onChange({ target: { value: v } });
      return (
        <div className="flex items-center justify-between gap-3 py-1" data-testid={`p-${f.key}-row`}>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium opacity-80">{labelFor(f)}</span>
            {f.hint && <span className="block text-[10px] opacity-55">{tp(f.hint, lang)}</span>}
          </span>
          <div className="flex shrink-0 overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
            <button type="button" onClick={() => set("Yes")} data-testid={`p-${f.key}-yes`}
              className={`px-2.5 py-1 ${val === "Yes" ? "bg-emerald-500 text-white" : "opacity-70"}`}>{tp("Yes", lang)}</button>
            <button type="button" onClick={() => set("No")} data-testid={`p-${f.key}-no`}
              className={`px-2.5 py-1 ${val === "No" ? "bg-[#DA1E28] text-white" : "opacity-70"}`}>{tp("No", lang)}</button>
          </div>
        </div>
      );
    }
    if (f.type === "treegroup") return <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">—</option>{groupOptions.map((grp) => <optgroup key={grp.tree} label={grp.tree}>{grp.values.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}</optgroup>)}</Select>;
    if (f.type === "deptname") return (
      <div>
        <Input label={labelFor(f)} list="dept-name-options" value={val || ""} maxLength={40} onChange={onChange} placeholder={tp("e.g. Operations, Finance…", lang)} data-testid={`p-${f.key}`} />
        <datalist id="dept-name-options">{deptOptions.map((d) => <option key={d} value={d} />)}</datalist>
      </div>
    );
    if (f.type === "rank") return <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}><option value="">{`— ${tp("unranked", lang)} —`}</option>{Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((L) => <option key={L} value={L}>{L}</option>)}</Select>;
    if (f.type === "grade") {
      const amt = gradeAmount(val);
      const addRaw = gnum(form.attrs?.admin_addition);
      const add = (form.attrs?.admin_addition_mode === "percent") ? (amt * addRaw / 100) : addRaw;
      const rt = gradeRate(val);
      const isSalary = rt === "salary";
      const freq = gradeFreq(val);
      const div = isSalary ? (FREQ_DIV[freq] || 1) : 1;
      const unit = rt === "hourly" ? tp("/hr", lang)
        : rt === "pay_once" ? " " + tp("once", lang)
        : rt === "month_day_1" ? tp("/mo (1st)", lang)
        : (FREQ_SHORT[freq] || "/yr");
      return (
        <div>
          <Select label={labelFor(f)} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`}>
            <option value="">—</option>
            {salaryGrades.map((g) => <option key={g.grade} value={g.grade}>{`Grade ${g.grade}${g.label ? ` — ${g.label}` : ""}`}</option>)}
          </Select>
          {val && <div className="mt-1 text-[11px] opacity-70" data-testid="p-grade-amount">
            Base {fmtMoney(amt / div)}{unit}{add ? ` + admin ${fmtMoney(add / div)}${unit} = ${fmtMoney((amt + add) / div)}${unit}` : ""}
            {isSalary && freq !== "annual" ? ` · ${fmtMoney(amt + add)}${tp("/yr", lang)}` : ""}
          </div>}
        </div>
      );
    }
    return <Input label={labelFor(f) + (f.max ? ` (${f.max})` : "")} maxLength={f.max || 60} value={val || ""} onChange={onChange} data-testid={`p-${f.key}`} />;
  };

  return (
    <div className="space-y-6 fadeup">
      <QuickTour
        appKey="family_tree"
        targetTestId="new-tree"
        title={tp("Welcome to Familigi™", lang)}
        lines={[
          tp("Start by clicking “New Tree” to create a Family or Generic tree.", lang),
          tp("Then use ＋ Add to add records, Share for a read-only link, and Manual for the full step-by-step guide.", lang),
        ]}
      />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl">{APP_FULL.family_tree}</h1>
        <Badge color="success" data-testid="family_tree-version">v{APP_VERSIONS.family_tree}</Badge>
        <LiveBackupIndicator app="family_tree" />
        <div className="ms-auto flex flex-wrap gap-2">
          <div className="flex rounded-md border border-black/15 dark:border-white/15 p-0.5">
            <button onClick={() => setView("cards")} data-testid="view-cards" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${view === "cards" ? "bg-[var(--primary)] text-white" : ""}`}><LayoutGrid size={15} /> {tp("Cards", lang)}</button>
            <button onClick={() => setView("diagram")} data-testid="view-diagram" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${view === "diagram" ? "bg-[var(--primary)] text-white" : ""}`}><Network size={15} /> {tp("Diagram", lang)}</button>
          </div>
          <Button variant="ghost" onClick={() => setShowDob((v) => !v)} data-testid="toggle-dob">{showDob ? <EyeOff size={16} /> : <Eye size={16} />} DOB</Button>
          <Button variant="ghost" onClick={() => setShowDod((v) => !v)} data-testid="toggle-dod">{showDod ? <EyeOff size={16} /> : <Eye size={16} />} DOD</Button>
          <Button variant="ghost" onClick={() => replayTour("family_tree")} title={tp("Replay quick tour", lang)} data-testid="ft-tour"><HelpCircle size={16} /></Button>
          <Button variant="ghost" onClick={() => downloadManual("family_tree")} data-testid="ft-manual"><Download size={16} /> {tp("Manual", lang)}</Button>
          <Button variant="ghost" onClick={exportCsv} disabled={!activeTree || isDemo} title={isDemo ? "Exporting is disabled in demo mode" : ""} data-testid="ft-csv"><FileSpreadsheet size={16} /> CSV</Button>
          <Button variant="ghost" onClick={exportJson} disabled={!activeTree || isDemo} title={isDemo ? "Exporting is disabled in demo mode" : ""} data-testid="ft-json"><Download size={16} /> JSON</Button>
          {activeTree && !isDemo && <ImportButton app="family_tree" label={tp("Import", lang)} extra={{ tree_id: activeTree }} onDone={() => window.location.reload()} />}
          <Button variant="ghost" onClick={exportGedcom} disabled={!activeTree || isDemo} title={isDemo ? "Exporting is disabled in demo mode" : "Export to GEDCOM (Ancestry / MyHeritage)"} data-testid="ft-gedcom-export"><GitBranch size={16} /> GEDCOM</Button>
          {!isDemo && <label className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-sm hover:bg-black/[0.05] dark:hover:bg-white/[0.06]" title="Import a GEDCOM file into a new tree" data-testid="ft-gedcom-import-label">
            {gedBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {tp("Import GEDCOM", lang)}
            <input type="file" accept=".ged,.gedcom,text/plain" className="hidden" onChange={(e) => importGedcom(e, "new")} disabled={gedBusy} data-testid="ft-gedcom-import" />
          </label>}
          {!isDemo && activeTree && <label className="inline-flex cursor-pointer items-center gap-1 rounded-md px-2.5 py-1.5 text-sm hover:bg-black/[0.05] dark:hover:bg-white/[0.06]" title="Merge a GEDCOM file into the open tree (duplicates matched by name + birth date)" data-testid="ft-gedcom-merge-label">
            {gedBusy ? <Loader2 size={16} className="animate-spin" /> : <GitMerge size={16} />} {tp("Merge GEDCOM", lang)}
            <input type="file" accept=".ged,.gedcom,text/plain" className="hidden" onChange={(e) => importGedcom(e, "merge")} disabled={gedBusy} data-testid="ft-gedcom-merge" />
          </label>}
          <Button variant="ghost" onClick={openPrint} disabled={!activeTree || isDemo} title={isDemo ? "Printing is disabled in demo mode" : ""} data-testid="ft-print"><Printer size={16} /> {tp("Print", lang)}</Button>
          {treeType === "generic" && <Button variant="ghost" onClick={() => { setHourlyQ(""); setHourlyOpen(true); }} data-testid="hourly-search-btn"><Users size={16} /> {tp("Hourly payees", lang)}</Button>}
          {treeType === "generic" && <Button variant="ghost" onClick={() => setPayrollOpen(true)} data-testid="payroll-csv-btn"><FileSpreadsheet size={16} /> {tp("Payroll CSV", lang)}</Button>}
          <Button onClick={openAdd} disabled={!activeTree} data-testid="add-person"><Plus size={16} /> {t("add", lang)}</Button>
        </div>
      </div>

      <AutomationCard app="family_tree" />

      <PoolAlertFlasher />

      {/* Tree bar */}
      <Card className="flex flex-wrap items-center gap-3">
        <TreePine size={18} className="text-[var(--primary)]" />
        <div className="min-w-[220px]">
          <Select value={activeTree} onChange={(e) => { setActiveTree(e.target.value); setFocus(null); }} data-testid="tree-select">
            <option value="">{tp("— select a tree —", lang)}</option>
            {trees.map((tr) => <option key={tr.id} value={tr.id}>{`${tr.name} · ${tr.type === "generic" ? "Generic" : "Family"}`}</option>)}
          </Select>
        </div>
        {tree && <Badge color={treeType === "generic" ? "info" : "success"} data-testid="tree-type-badge">{treeType === "generic" ? "GENERIC" : "FAMILY"} option</Badge>}
        <span className="text-xs opacity-60">{activeTree ? inTree.length : 0} record(s)</span>
        <div className="ms-auto flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => setTreeModal(true)} data-testid="new-tree"><Plus size={15} /> {tp("New Tree", lang)}</Button>
          {tree && <Button variant="ghost" onClick={openShare} data-testid="share-tree"><Share2 size={15} /> {tp("Share", lang)}</Button>}
          {tree && (
            <Button variant="ghost" onClick={openSuggestions} data-testid="suggestions-btn" className="relative">
              <Inbox size={15} /> {tp("Suggestions", lang)}
              {pending.length > 0 && <span className="ms-1 rounded-full bg-[#DA1E28] px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="suggestions-count">{pending.length}</span>}
            </Button>
          )}
          {tree && otherTrees.length > 0 && <Button variant="ghost" onClick={openMerge} data-testid="merge-tree"><GitMerge size={15} /> {tp("Merge", lang)}</Button>}
          {tree && <Button variant="ghost" onClick={openAutoLink} data-testid="auto-link-btn" title={tp("Read typed Spouse/Mother/Father names and create structural links", lang)}><Link2 size={15} /> {tp("Auto-link", lang)}</Button>}
          {tree && <Button variant="ghost" onClick={openLabels} data-testid="edit-labels"><Settings2 size={15} /> {tp("Field labels", lang)}</Button>}
          {tree && treeType === "family" && <Button variant="ghost" onClick={() => setBranchPanel(true)} data-testid="branches-btn" title={tp("View and manage every branch in this tree", lang)}><GitBranch size={15} /> {tp("Branches", lang)}</Button>}
          {tree && <Button variant="ghost" onClick={() => { setDemoMsg(""); setDemoOpen(true); }} data-testid="demographics-btn" title={tp("Counts by life status and age band, per branch", lang)}><Users size={15} /> {tp("Demographics", lang)}</Button>}
          {tree && treeType === "generic" && <Button variant="ghost" onClick={() => setGradesOpen(true)} data-testid="salary-grades-btn" title={tp("Edit the 1–75 salary grade amounts", lang)}><Coins size={15} /> {tp("Salary grades", lang)}</Button>}
          {tree && <button onClick={openDelTree} data-testid="del-tree" className="opacity-60 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={16} /></button>}
        </div>
      </Card>

      {orphans > 0 && (
        <Card className="flex flex-wrap items-center gap-3 border border-[#FF832B]/40" data-testid="orphan-banner">
          <span className="text-sm">⚠️ {orphans} {tp("record(s) belong to a deleted tree. Move them into a tree or remove them.", lang)}</span>
          <div className="ms-auto flex flex-wrap items-center gap-2">
            <div className="min-w-[180px]">
              <Select value={orphanTarget} onChange={(e) => setOrphanTarget(e.target.value)} data-testid="orphan-target">
                <option value="">{tp("— choose a tree —", lang)}</option>
                {trees.map((tr) => <option key={tr.id} value={tr.id}>{tr.name}</option>)}
              </Select>
            </div>
            <Button variant="ghost" onClick={() => cleanupOrphans("reassign")} disabled={!orphanTarget} data-testid="orphan-reassign">{tp("Move here", lang)}</Button>
            <button onClick={() => cleanupOrphans("delete")} data-testid="orphan-delete" className="text-sm opacity-60 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={15} className="inline" /> {tp("Delete all", lang)}</button>
          </div>
        </Card>
      )}

      {!activeTree && <Card className="opacity-70">{tp("Create or select a tree to begin. Each tree is either a Family or Generic type and holds its own records.", lang)}</Card>}

      {activeTree && (
        <>
        <div className="flex flex-col gap-5 xl:flex-row">
        <div className="min-w-0 flex-1 space-y-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tp("Search names, dates, or any field…", lang)}
              className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-9 pe-3 text-sm" data-testid="ft-search" />
          </div>

          {/* Selection & bulk-action toolbar */}
          <Card className="flex flex-wrap items-center gap-2 py-2" data-testid="selection-toolbar">
            <button onClick={selCount ? clearSel : selectAllFiltered} data-testid="sel-toggle-all"
              className="flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100">
              {selCount ? <CheckSquare size={16} className="text-[var(--primary)]" /> : <Square size={16} />}
              {selCount ? "Clear" : "Select all"}
            </button>
            <span className="text-sm font-medium" data-testid="sel-count">{selCount} selected</span>
            <div className="ms-auto flex flex-wrap gap-2">
              <Button variant="ghost" disabled={selCount !== 1} onClick={editSelected} title={selCount > 1 ? "Select a single entry to edit" : "Edit selected entry"} data-testid="sel-edit"><Pencil size={15} /> Edit</Button>
              <Button variant="ghost" onClick={() => { setResyncAllMsg(""); setGroupsModal(true); }} data-testid="groups-view"><Users size={15} /> Groups{groups.length ? ` (${groups.length})` : ""}</Button>
              <Button variant="ghost" disabled={!selCount} onClick={() => { setGroupName(""); setGroupModal(true); }} data-testid="sel-group"><FolderPlus size={15} /> Create group</Button>
              <Button variant="ghost" disabled={!selCount} onClick={openMove} data-testid="sel-move"><FolderInput size={15} /> Move</Button>
              <Button variant="ghost" disabled={!selCount} onClick={() => { setListTitle(""); setListModal(true); }} data-testid="sel-list"><FileSpreadsheet size={15} /> List</Button>
              <Button variant="danger" disabled={!selCount} onClick={() => setBulkDelModal(true)} data-testid="sel-delete"><Trash2 size={15} /> Delete</Button>
            </div>
          </Card>

          {view === "diagram" && treeType === "generic" && (
            <>
              <p className="text-xs opacity-60">Org chart — each department/unit has a blue name banner; units are ordered by their Group rank (A–Z) and people within a unit by Salary Grade. Switch between <b>Department/Unit View</b> (chart) and <b>Unit Rank View</b> (vertical salary list) using the header controls.</p>
              <RankBoard people={filtered} tree={tree} onPersist={persistRank} onEdit={openEdit} onIdeaFilter={setIdeaFilter} rawAge={rawAge} nameOf={nameOf} gradeAmount={gradeAmount} fmtMoney={fmtMoney} />
            </>
          )}
          {view === "diagram" && treeType !== "generic" && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs opacity-60">Orientation:</span>
                <div className="flex rounded-md border border-black/15 dark:border-white/15 p-0.5">
                  <button onClick={() => setOrientation("portrait")} data-testid="orient-portrait" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${orientation === "portrait" ? "bg-[var(--primary)] text-white" : ""}`}><MoveVertical size={14} /> Portrait</button>
                  <button onClick={() => setOrientation("landscape")} data-testid="orient-landscape" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${orientation === "landscape" ? "bg-[var(--primary)] text-white" : ""}`}><MoveHorizontal size={14} /> Landscape</button>
                </div>
                <span className="text-xs opacity-50">{orientation === "landscape" ? "Generations flow left → right" : "Generations flow top → bottom"}</span>
                <Button variant="ghost" onClick={resetLayout} data-testid="reset-layout" title={tp("Clear any dragged positions and auto-arrange the whole tree", lang)}><RefreshCw size={14} /> {tp("Auto-arrange", lang)}</Button>
              </div>
              <p className="text-xs opacity-60">Drag nodes to reposition · drag from a node's bottom/side dot to another node's dot to set it as the parent.</p>
              <FamilyDiagram people={filtered} descendants={descendants} focus={focus} showMaiden={false} onConnectParent={setParent} onMove={moveNode} orientation={orientation} selectedIds={selected} onToggleSelect={toggleSel} rtl={lang === "ar"} />
            </>
          )}

          {view === "cards" && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.length === 0 && <Card className="opacity-60">{tp("No records match.", lang)}</Card>}
              {filtered.map((p) => {
                const isSel = selected.has(p.id);
                const titleField = fields.find((f) => f.key === "title" || f.key === "spouse_first");
                return (
                <Card key={p.id} className={`space-y-2 ${isSel ? "select-flash" : ""}`} data-testid={`person-${p.id}`}
                  style={{ borderInlineStart: `4px solid ${p.color || "#0F62FE"}`, outline: descendants.has(p.id) ? `2px solid ${p.color || "#0F62FE"}` : "none" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <button onClick={() => toggleSel(p.id)} data-testid={`select-person-${p.id}`} aria-pressed={isSel}
                        className="shrink-0 opacity-70 hover:opacity-100">
                        {isSel ? <CheckSquare size={18} className="text-[var(--primary)]" /> : <Square size={18} />}
                      </button>
                      {p.photo && (
                        <button onClick={() => toggleSel(p.id)} data-testid={`select-photo-${p.id}`}
                          className={`h-10 w-10 shrink-0 overflow-hidden rounded-full border ${isSel ? "border-[var(--primary)]" : "border-black/10 dark:border-white/15"}`}>
                          <img src={p.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${p.photo_rot || 0}deg)` }} />
                        </button>
                      )}
                      <button onClick={() => toggleSel(p.id)} data-testid={`select-name-${p.id}`}
                        className="font-head text-lg truncate text-start hover:text-[var(--primary)]">{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</button>
                    </div>
                    <div className="flex gap-1">
                      {!isDemo && <AttachmentsButton coll="persons" recordId={p.id} compact />}
                      <button onClick={() => openEdit(p)} data-testid={`edit-person-${p.id}`} className="opacity-50 hover:opacity-100"><Pencil size={15} /></button>
                      <button onClick={() => del(p.id)} data-testid={`del-person-${p.id}`} className="opacity-50 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {treeType === "family" && <Badge color="info"><GitBranch size={11} className="inline" /> {p.branch || "Main"}</Badge>}
                    {showDob && p.dob && <Badge>b. {weekday(p.dob)} {p.dob}</Badge>}
                    {showDod && p.dod && <Badge color="danger">d. {weekday(p.dod)} {p.dod}</Badge>}
                    {treeType === "generic" && rawAge(p) != null && <Badge color="info" data-testid={`age-${p.id}`}>Age {rawAge(p)}</Badge>}
                  </div>
                  <div className="space-y-0.5 text-xs opacity-80">
                    {fields.map((f) => {
                      if (f.noCard) return null;
                      const v = p.attrs?.[f.key];
                      if (!v) return null;
                      const clickable = f.key === "title";
                      return <div key={f.key}><span className="opacity-60">{labelFor(f)}:</span>{" "}
                        {clickable
                          ? <button onClick={() => toggleSel(p.id)} data-testid={`select-title-${p.id}`} className="hover:text-[var(--primary)] hover:underline">{v}</button>
                          : v}</div>;
                    })}
                  </div>
                  <button onClick={() => setFocus(focus === p.id ? null : p.id)} data-testid={`focus-${p.id}`}
                    className="text-xs text-[var(--primary)] hover:underline">{focus === p.id ? "Clear highlight" : "Highlight descendants"}</button>
                </Card>
                );
              })}
            </div>
          )}
        </div>
        <NearestPanel people={inTree} nameOf={nameOf} />
        </div>
        </>
      )}

      {/* Add/Edit person */}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit record" : "Add record"} testid="person-modal" draggable>
        <div className="space-y-3">
          <div className="flex items-center gap-3 rounded-md border border-black/10 dark:border-white/10 p-2">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-black/15 dark:border-white/15 bg-black/5 dark:bg-white/5 flex items-center justify-center">
              {form.photo
                ? <img src={form.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${form.photo_rot || 0}deg) scale(1.42)`, transformOrigin: "center center" }} data-testid="p-photo-preview" />
                : <ImagePlus size={22} className="opacity-40" />}
            </div>
            <div className="flex-1 space-y-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5">
                <ImagePlus size={15} /> {form.photo ? "Change photo" : "Add photo"}
                <input type="file" accept="image/*" className="hidden" data-testid="p-photo"
                  onChange={(e) => readAndResize(e.target.files?.[0], (url) => setForm((f) => ({ ...f, photo: url, photo_rot: 0 })))} />
              </label>
              {form.photo && (
                <div className="flex items-center gap-2">
                  <RotateCw size={14} className="opacity-60" />
                  <input type="range" min={0} max={360} value={form.photo_rot || 0} data-testid="p-photo-rot"
                    onChange={(e) => setForm({ ...form, photo_rot: Number(e.target.value) })} className="flex-1 accent-[var(--primary)]" />
                  <span className="w-10 text-end text-xs tabular-nums opacity-70">{form.photo_rot || 0}°</span>
                  <button type="button" onClick={() => setForm({ ...form, photo: "", photo_rot: 0 })} data-testid="p-photo-remove" className="text-xs opacity-60 hover:text-[#DA1E28]"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input label={tp("First (15)", lang)} maxLength={15} value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} data-testid="p-first" />
            <Input label={tp("Middle (15)", lang)} maxLength={15} value={form.middle} onChange={(e) => setForm({ ...form, middle: e.target.value })} data-testid="p-middle" />
            <Input label={tp("Last (15)", lang)} maxLength={15} value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} data-testid="p-last" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {renderDateInput("dob", tp("Date of birth", lang), form.dob, (v) => setForm((f) => ({ ...f, dob: v })))}
            {renderDateInput("dod", tp("Date of death", lang), form.dod, (v) => setForm((f) => ({ ...f, dod: v })))}
          </div>
          {treeType === "family" && (
            <div className="grid grid-cols-2 gap-2">
            <div>
              <Input label={tp("Branch", lang)} value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} data-testid="p-branch" />
              {(() => {
                const br = (form.branch || "").trim();
                const others = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "").trim() === br && p.id !== editId).length;
                return br && others > 0 ? (
                  <button type="button" onClick={() => setRenamePrompt({ open: true, from: br, name: br })} data-testid="rename-branch-open"
                    className="mt-1 text-xs text-[var(--primary)] hover:underline">
                    <GitBranch size={11} className="inline" /> {tp(`Rename “${br}” for all ${others + 1} members`, lang)}
                  </button>
                ) : null;
              })()}
            </div>
              <div>
                <span className="mb-1 block text-xs font-medium opacity-70">{tp("Branch color", lang)}</span>
                <div className="flex flex-wrap items-center gap-1.5" data-testid="p-color-swatches">
                  {BRANCH_COLORS.map((c) => (
                    <button key={c.hex} type="button" title={c.name} aria-label={c.name}
                      onClick={() => pickColor(c.hex)} data-testid={`p-color-${c.name.toLowerCase()}`}
                      className={`h-7 w-7 rounded-full transition ${form.color === c.hex ? "ring-2 ring-offset-2 ring-black/60 dark:ring-white/80 dark:ring-offset-neutral-900" : "ring-1 ring-black/10"}`}
                      style={{ background: c.hex }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          {(treeType === "generic" ? ["Occupation", "Compensation", "IDEA"] : [null]).map((sec) => {
            const secFields = fields.filter((f) => (f.section || null) === sec);
            if (!secFields.length) return null;
            return (
              <div key={sec || "family"} className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid={`fields-section-${(sec || "family").toLowerCase()}`}>
                <div className="mb-2 text-xs font-semibold opacity-70">{sec ? (SECTION_LABELS[sec] || sec) : (treeType === "generic" ? "Generic fields" : "Family fields")}</div>
                <div className="grid grid-cols-2 gap-2">
                  {secFields.map((f) => (
                    <div key={f.key} className={f.full ? "col-span-2" : ""}>
                      {!f.label && (
                        <input defaultValue={tree?.labels?.[f.key] || ""} placeholder={tp("Name this field…", lang)} maxLength={40}
                          onBlur={(e) => saveOneLabel(f.key, e.target.value.trim())} data-testid={`p-fieldname-${f.key}`}
                          className="mb-1 w-full rounded border border-dashed border-[var(--primary)]/50 bg-transparent px-1.5 py-1 text-[11px] font-medium placeholder:opacity-50 focus:outline-none focus:ring-1 focus:ring-[var(--primary)]" />
                      )}
                      {renderField(f, form.attrs?.[f.key], (e) => setForm({ ...form, attrs: { ...form.attrs, [f.key]: e.target.value } }))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {treeType === "family" ? (
            <div className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid="relationship-box">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold opacity-70">{tp("Relationships", lang)} — {tp("auto-positions this entry in the tree", lang)}</span>
                <select value={linkSort} onChange={(e) => setLinkSort(e.target.value)} data-testid="link-sort"
                  className="rounded border border-black/10 bg-transparent px-1.5 py-0.5 text-[11px] opacity-80 dark:border-white/15" title={tp("Sort the people lists below", lang)}>
                  <option value="added">{tp("Sort: last added", lang)}</option>
                  <option value="first">{tp("Sort: first name A–Z", lang)}</option>
                  <option value="middle">{tp("Sort: middle name A–Z", lang)}</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select label={tp("Child of", lang)} value={form.parent_id} onChange={(e) => pickParent(e.target.value)} data-testid="p-child-of">
                  <option value="">— none —</option>
                  {sortedInTree.filter((p) => p.id !== editId).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                </Select>
                <Select label={tp("Second parent", lang)} value={form.parent_id2} onChange={(e) => setForm({ ...form, parent_id2: e.target.value })} data-testid="p-parent2">
                  <option value="">— none —</option>
                  {sortedInTree.filter((p) => p.id !== editId).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                </Select>
                <Select label={tp("Sibling to", lang)} value="" onChange={(e) => addSibling(e.target.value)} data-testid="p-sibling-of">
                  <option value="">{`— ${tp("link a brother/sister (no parents needed)", lang)} —`}</option>
                  {sortedInTree.filter((p) => p.id !== editId && !(form.sibling_ids || []).includes(p.id)).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                </Select>
                <Select label={tp("Descend line from", lang)} value={form.descend_from || "both"} onChange={(e) => setForm({ ...form, descend_from: e.target.value })} data-testid="p-descend-from">
                  <option value="both">{tp("Both parents", lang)}</option>
                  {form.parent_id && <option value="parent">{`${tp("From", lang)} ${nameById(form.parent_id)}`}</option>}
                  {form.parent_id2 && <option value="parent2">{`${tp("From", lang)} ${nameById(form.parent_id2)}`}</option>}
                </Select>
              </div>
              <div className="mt-3">
                <span className="mb-1 block text-xs font-medium opacity-70">{tp("Spouse(s)", lang)} — {tp("polygamous households supported (up to 9); spouses render in a row beside this person", lang)}</span>
                <div className="mb-1.5 flex flex-wrap gap-1.5" data-testid="p-spouse-chips">
                  {(form.spouse_ids || []).length === 0 && <span className="text-xs opacity-50">{tp("No spouse linked", lang)}</span>}
                  {(form.spouse_ids || []).map((sid) => {
                    const s = people.find((p) => p.id === sid);
                    return (
                      <span key={sid} data-testid={`p-spouse-chip-${sid}`} className="inline-flex items-center gap-1 rounded-full bg-[#D02670]/10 px-2 py-0.5 text-xs text-[#D02670]">
                        ⚭ {s ? nameOf(s) : "—"}
                        <button type="button" onClick={() => removeSpouse(sid)} data-testid={`p-spouse-remove-${sid}`} className="opacity-70 hover:opacity-100"><X size={11} /></button>
                      </span>
                    );
                  })}
                </div>
                <SpouseSortToggle value={spouseSort} onChange={setSpouseSort} idBase="spouse-sort" />
                <Select label={tp("Add spouse", lang)} value="" onChange={(e) => addSpouse(e.target.value)} disabled={(form.spouse_ids || []).length >= 9} data-testid="p-spouse-add">
                  <option value="">{`— ${(form.spouse_ids || []).length >= 9 ? tp("maximum of 9 spouses reached", lang) : tp("select a person to add as spouse", lang)} —`}</option>
                  {sortPeopleBy(inTree, spouseSort).filter((p) => p.id !== editId && !(form.spouse_ids || []).includes(p.id)).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                </Select>
                <div className="mt-2">
                  <div className="mb-1 flex flex-wrap gap-1.5" data-testid="p-former-spouse-chips">
                    {(form.former_spouse_ids || []).map((sid) => {
                      const s = people.find((p) => p.id === sid);
                      return (
                        <span key={sid} data-testid={`p-former-spouse-chip-${sid}`} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-xs opacity-80 dark:bg-white/10">
                          ⚭̶ {tp("former", lang)}: {s ? nameOf(s) : "—"}
                          <button type="button" onClick={() => removeFormerSpouse(sid)} data-testid={`p-former-spouse-remove-${sid}`} className="opacity-70 hover:opacity-100"><X size={11} /></button>
                        </span>
                      );
                    })}
                  </div>
                  <SpouseSortToggle value={formerSpouseSort} onChange={setFormerSpouseSort} idBase="former-spouse-sort" />
                  <Select label={tp("Add former spouse", lang)} value="" onChange={(e) => addFormerSpouse(e.target.value)} data-testid="p-former-spouse-add">
                    <option value="">— {tp("select a person to add as a former spouse", lang)} —</option>
                    {sortPeopleBy(inTree, formerSpouseSort).filter((p) => p.id !== editId && !(form.former_spouse_ids || []).includes(p.id)).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                  </Select>
                </div>
                {(form.sibling_ids || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="p-sibling-chips">
                    <span className="w-full text-xs font-medium opacity-70">{tp("Siblings", lang)}</span>
                    {(form.sibling_ids || []).map((sid) => {
                      const s = people.find((p) => p.id === sid);
                      return (
                        <span key={sid} data-testid={`p-sibling-chip-${sid}`} className="inline-flex items-center gap-1 rounded-full bg-[#0F62FE]/10 px-2 py-0.5 text-xs text-[#0F62FE]">
                          {s ? nameOf(s) : "—"}
                          <button type="button" onClick={() => removeSibling(sid)} data-testid={`p-sibling-remove-${sid}`} className="opacity-70 hover:opacity-100"><X size={11} /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="mt-3 border-t border-black/10 pt-2 dark:border-white/10" data-testid="other-relationships">
                <span className="mb-1 block text-xs font-medium opacity-70">{tp("Other relationships", lang)}</span>
                <div className="flex flex-wrap items-end gap-2">
                  <Select label={tp("Type", lang)} value={relType} onChange={(e) => setRelType(e.target.value)} data-testid="p-rel-type">
                    <option value="cousin">{tp("Cousin", lang)}</option>
                    <option value="common_ancestor">{tp("Common ancestor", lang)}</option>
                  </Select>
                  <Select label={tp("Person", lang)} value={relPerson} onChange={(e) => setRelPerson(e.target.value)} data-testid="p-rel-person">
                    <option value="">— {tp("select a person", lang)} —</option>
                    {sortedInTree.filter((p) => p.id !== editId).map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
                  </Select>
                  <Button type="button" variant="ghost" onClick={addRelation} disabled={!relPerson} data-testid="p-rel-add"><Plus size={14} /> {tp("Add", lang)}</Button>
                </div>
                {(form.relations || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="p-rel-chips">
                    {(form.relations || []).map((r, i) => {
                      const s = people.find((p) => p.id === r.id);
                      return (
                        <span key={i} data-testid={`p-rel-chip-${i}`} className="inline-flex items-center gap-1 rounded-full bg-black/5 px-2 py-0.5 text-xs dark:bg-white/10">
                          {r.type === "cousin" ? tp("Cousin", lang) : tp("Common ancestor", lang)}: {s ? nameOf(s) : "—"}
                          <button type="button" onClick={() => removeRelation(i)} data-testid={`p-rel-remove-${i}`} className="opacity-70 hover:opacity-100"><X size={11} /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            {editId && treeType === "generic" && form.attrs?.prompt_rate_card === "Yes" && (
              <Button variant="ghost" onClick={() => setRateCardOpen(true)} data-testid="open-rate-card" className="me-auto">
                <Coins size={15} /> {tp("Timesheet", lang)}
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel", lang)}</Button>
            <Button onClick={save} data-testid="save-person">{t("save", lang)}</Button>
          </div>
        </div>
      </Modal>
      <RateCard
        open={rateCardOpen}
        onClose={() => setRateCardOpen(false)}
        personId={editId}
        treeId={activeTree}
        lang={lang}
        gradeNum={form.attrs?.salary_grade || ""}
        header={{
          location: form.attrs?.location || "",
          principal_group: groupLabel(form.attrs?.principal_group),
          secondary_group: groupLabel(form.attrs?.secondary_group),
          first: form.first || "",
          last: form.last || "",
          grade: form.attrs?.salary_grade ? `Grade ${form.attrs.salary_grade}` : "",
        }}
      />
      <Modal open={hourlyOpen} onClose={() => setHourlyOpen(false)} title={tp("Hourly payees", lang)} testid="hourly-search-modal">
        <div className="space-y-3">
          <input value={hourlyQ} onChange={(e) => setHourlyQ(e.target.value)} placeholder={tp("Search by name…", lang)}
            data-testid="hourly-search-input" className="w-full rounded border border-black/15 bg-transparent px-3 py-2 text-sm dark:border-white/15" />
          {(() => {
            const q = hourlyQ.trim().toLowerCase();
            const rows = inTree.filter((p) => p.attrs?.pay_hourly === "Yes" && (!q || nameOf(p).toLowerCase().includes(q)));
            if (!rows.length) return <p className="py-4 text-center text-sm opacity-60" data-testid="hourly-empty">{tp("No hourly payees found. Set 'Hourly payee' = Yes on a person's Compensation tab.", lang)}</p>;
            return (
              <div className="max-h-80 space-y-1 overflow-y-auto" data-testid="hourly-list">
                {rows.map((p) => (
                  <button key={p.id} onClick={() => { setHourlyOpen(false); openEdit(p); }} data-testid={`hourly-row-${p.id}`}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-black/10 px-3 py-2 text-start lift hover:border-[var(--primary)] dark:border-white/12">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{nameOf(p)}</span>
                      <span className="block text-[11px] opacity-60">{p.attrs?.salary_grade ? `Grade ${p.attrs.salary_grade}` : "—"}{p.attrs?.location ? ` · ${p.attrs.location}` : ""}</span>
                    </span>
                    <Pencil size={14} className="shrink-0 opacity-60" />
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Auto-link relationships from typed names */}
      <Modal open={autoLinkModal} onClose={() => setAutoLinkModal(false)} title={tp("Auto-link relationships", lang)} testid="auto-link-modal" wide>
        <div className="space-y-3">
          <p className="text-sm opacity-70">{tp("This reads the typed Spouse / Mother / Father names on each record, matches them to existing people in this tree, and creates the structural links the diagram uses to arrange everyone.", lang)}</p>
          {autoLinkPlan.length === 0 ? (
            <Card className="opacity-60" data-testid="auto-link-none">{tp("No new links found. Everyone with a typed spouse/parent name is already linked, or the names don't match another record exactly.", lang)}</Card>
          ) : (
            <div className="max-h-[45vh] space-y-2 overflow-auto pe-1" data-testid="auto-link-plan">
              {autoLinkPlan.map((it) => (
                <Card key={it.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`auto-link-row-${it.id}`}>
                  <span className="font-head">{it.name}</span>
                  {it.spouse && <Badge color="danger">⚭ {tp("spouse", lang)}: {it.spouse.name}</Badge>}
                  {it.father && <Badge color="info">{tp("father", lang)}: {it.father.name}</Badge>}
                  {it.mother && <Badge color="info">{tp("mother", lang)}: {it.mother.name}</Badge>}
                </Card>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setAutoLinkModal(false)} data-testid="auto-link-cancel">{t("cancel", lang)}</Button>
            <Button onClick={applyAutoLink} disabled={autoLinkBusy || autoLinkPlan.length === 0} data-testid="auto-link-apply"><Link2 size={15} /> {autoLinkBusy ? tp("Linking…", lang) : `${tp("Link", lang)} ${autoLinkPlan.length}`}</Button>
          </div>
        </div>
      </Modal>

      {/* Branches manager */}
      <Modal open={branchPanel} onClose={() => setBranchPanel(false)} title={tp("Branches in this tree", lang)} testid="branches-modal" wide>
        <div className="space-y-2">
          {(() => {
            const rows = {};
            people.filter((p) => (activeTree ? p.tree_id === activeTree : true)).forEach((p) => {
              const b = (p.branch || "Main").trim() || "Main";
              if (!rows[b]) rows[b] = { name: b, count: 0, colors: {} };
              rows[b].count += 1;
              const col = p.color || "#0F62FE";
              rows[b].colors[col] = (rows[b].colors[col] || 0) + 1;
            });
            const list = Object.values(rows)
              .map((b) => ({ ...b, color: Object.entries(b.colors).sort((a, z) => z[1] - a[1])[0][0], mixed: Object.keys(b.colors).length > 1 }))
              .sort((a, z) => a.name.localeCompare(z.name));
            if (!list.length) return <p className="text-sm opacity-60">{tp("No people in this tree yet.", lang)}</p>;
            return list.map((b) => (
              <div key={b.name} className="flex flex-wrap items-center gap-3 rounded-md border border-black/10 p-2.5 dark:border-white/10" data-testid={`branch-row-${b.name}`}>
                <span className="h-6 w-6 shrink-0 rounded-full ring-1 ring-black/10" style={{ background: b.color }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{b.name}</div>
                  <div className="text-xs opacity-60">{b.count} {b.count === 1 ? tp("member", lang) : tp("members", lang)}{b.mixed ? ` · ${tp("mixed colours", lang)}` : ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  {BRANCH_COLORS.map((c) => (
                    <button key={c.hex} type="button" title={tp(`Recolour all “${b.name}” to ${c.name}`, lang)} onClick={() => recolourBranch(b.name, c.hex)}
                      data-testid={`branch-recolour-${b.name}-${c.name}`}
                      className="h-5 w-5 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110" style={{ background: c.hex }} />
                  ))}
                </div>
                <Button variant="ghost" onClick={() => { setBranchPanel(false); setRenamePrompt({ open: true, from: b.name, name: b.name }); }} data-testid={`branch-rename-${b.name}`}><GitBranch size={13} /> {tp("Rename", lang)}</Button>
                {list.length > 1 && (
                  <select defaultValue="" onChange={(e) => { const to = e.target.value; e.target.value = ""; if (to) mergeBranch(b.name, to); }}
                    data-testid={`branch-merge-${b.name}`} title={tp("Fold this branch into another", lang)}
                    className="rounded border border-black/10 bg-transparent px-1.5 py-1 text-xs dark:border-white/15">
                    <option value="">{tp("Merge into…", lang)}</option>
                    {list.filter((o) => o.name !== b.name).map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
                  </select>
                )}
              </div>
            ));
          })()}
          <p className="pt-1 text-xs opacity-60">{tp("Recolour applies instantly to every member of that branch. “Rename” changes the branch name on all its members.", lang)}</p>
        </div>
      </Modal>

      {/* Demographics: counts by life status + age band, per branch */}
      <Modal open={demoOpen} onClose={() => setDemoOpen(false)} title={tp("Demographics — counts by branch", lang)} testid="demographics-modal" wide>
        <div className="space-y-4">
          <div className="rounded-md border border-black/10 p-2.5 dark:border-white/10">
            <div className="mb-2 text-xs font-semibold opacity-70">{tp("Age bands (completed years; edit as needed — 3 spare slots)", lang)}</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {ageBands.map((b, i) => (
                <div key={i} className="rounded border border-black/10 p-1.5 dark:border-white/10" data-testid={`demo-band-${i}`}>
                  <input value={b.label} placeholder={tp("Label", lang)} maxLength={20}
                    onChange={(e) => setAgeBands((a) => a.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                    data-testid={`demo-band-label-${i}`} className="mb-1 w-full rounded border border-black/10 bg-transparent px-1.5 py-0.5 text-[11px] font-medium dark:border-white/15" />
                  <div className="flex items-center gap-1">
                    <input type="number" value={b.min} placeholder={tp("min", lang)} onChange={(e) => setAgeBands((a) => a.map((x, j) => j === i ? { ...x, min: e.target.value } : x))}
                      data-testid={`demo-band-min-${i}`} className="w-full rounded border border-black/10 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                    <span className="text-[11px] opacity-50">–</span>
                    <input type="number" value={b.max} placeholder={tp("max", lang)} onChange={(e) => setAgeBands((a) => a.map((x, j) => j === i ? { ...x, max: e.target.value } : x))}
                      data-testid={`demo-band-max-${i}`} className="w-full rounded border border-black/10 bg-transparent px-1 py-0.5 text-[11px] dark:border-white/15" />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] opacity-55">{tp("A person falls in the first band whose min ≤ age ≤ max. 65+ = min 65, max 200.", lang)}</span>
              <div className="flex items-center gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium" data-testid="demo-living-only-label">
                  <input type="checkbox" checked={demoLivingOnly} onChange={(e) => setDemoLivingOnly(e.target.checked)} data-testid="demo-living-only" className="accent-[var(--primary)]" />
                  {tp("Count living only in age bands (payee view)", lang)}
                </label>
                <Button variant="ghost" onClick={saveDemoBands} data-testid="demo-save-bands"><Check size={14} /> {tp("Save bands to this tree", lang)}</Button>
              </div>
            </div>
          </div>
          {(() => {
            const inT = people.filter((p) => (activeTree ? p.tree_id === activeTree : true));
            const activeBands = ageBands.filter((b) => b.min !== "" && b.max !== "");
            const calc = (arr) => {
              const r = { living: 0, deceased: 0, unknown: 0, total: arr.length, bands: activeBands.map(() => 0) };
              arr.forEach((p) => {
                if (p.dod) r.deceased++; else r.living++;
                if (demoLivingOnly && p.dod) return;
                const age = ageOf(p);
                if (age === null) { r.unknown++; return; }
                const idx = activeBands.findIndex((b) => age >= Number(b.min) && age <= Number(b.max));
                if (idx >= 0) r.bands[idx]++; else r.unknown++;
              });
              return r;
            };
            const groups = {};
            inT.forEach((p) => { const b = (p.branch || "Main").trim() || "Main"; (groups[b] = groups[b] || []).push(p); });
            const names = Object.keys(groups).sort();
            const cell = "px-2 py-1 text-center tabular-nums";
            const row = (name, branchKey, arr, bold) => {
              const r = calc(arr);
              return (
                <tr key={name} data-testid={`demo-row-${name}`} className={bold ? "border-t-2 border-black/20 font-semibold dark:border-white/20" : "border-t border-black/5 dark:border-white/5"}>
                  <td className="px-2 py-1 text-start">{name}</td>
                  <td className={cell}>{r.total}</td>
                  <td className={`${cell} text-[#24A148]`}>{r.living}</td>
                  <td className={`${cell} opacity-70`}>{r.deceased}</td>
                  {r.bands.map((n, i) => (
                    <td key={i} className={cell}>
                      {n > 0 ? (
                        <button onClick={() => createBandGroup(branchKey, name, activeBands[i], arr)} data-testid={`demo-mkgroup-${name}-${i}`}
                          title={tp("Create a benefit/payee group from these people", lang)}
                          className="rounded px-1.5 font-semibold text-[var(--primary)] hover:bg-[var(--primary)]/10">{n}</button>
                      ) : <span className="opacity-40">0</span>}
                    </td>
                  ))}
                  <td className={`${cell} opacity-60`}>{r.unknown}</td>
                </tr>
              );
            };
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="demographics-table">
                  <thead>
                    <tr className="opacity-60">
                      <th className="px-2 py-1 text-start">{tp("Branch", lang)}</th>
                      <th className={cell}>{tp("Total", lang)}</th>
                      <th className={cell}>{tp("Living", lang)}</th>
                      <th className={cell}>{tp("Deceased", lang)}</th>
                      {activeBands.map((b, i) => <th key={i} className={cell}>{b.label || `${b.min}–${b.max}`}</th>)}
                      <th className={cell}>{tp("Unknown age", lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {names.map((n) => row(n, n, groups[n], false))}
                    {row(tp("All branches", lang), "__ALL__", inT, true)}
                  </tbody>
                </table>
              </div>
            );
          })()}
          {demoMsg && <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/10 px-3 py-2 text-xs font-medium" data-testid="demo-msg">{demoMsg}</div>}
          <p className="text-[11px] opacity-55">{tp("Living/Deceased is by presence of a Date of Death. Age bands are computed from Date of Birth (rounded down to completed years); people without a DOB show under “Unknown age”. Tick “Count living only” to make each age band a ready payee list (deceased excluded from the bands). Click any age-band count to save those exact people as a reusable benefit/payee group (appears under “Groups”). These groups will feed benefit-payment scheduling once the apps are cross-linked in the cluster.", lang)}</p>
        </div>
      </Modal>

      {/* Salary grades editor (generic/org trees) — central 1–25 → amount table */}
      <Modal open={gradesOpen} onClose={closeGrades} title={tp("Salary grades (1–75)", lang)} testid="salary-grades-modal" wide>
        <div className="space-y-3">
          <p className="text-[11px] opacity-60">{tp("Set each grade's amount. Salary amounts are ANNUAL and are auto-converted to Monthly, Semi-monthly, Biweekly and Weekly. Use the pay-type dropdown to pick Salary, Hourly, Pay once or Month day 1. Add an optional label per grade.", lang)}</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-medium opacity-70">{tp("View whole table as:", lang)}</span>
            <select value={gradeView} onChange={(e) => setGradeView(e.target.value)} data-testid="grade-view-select"
              className="rounded border border-black/15 bg-transparent px-2 py-1 text-[11px] dark:border-white/15">
              {PAY_FREQ.map((f) => <option key={f.key} value={f.key}>{tp(f.label, lang)}</option>)}
            </select>
            <button type="button" data-testid="grade-popup-suppress-all"
              onClick={() => { if (popupSuppressed) { localStorage.removeItem("grade_popup_suppressed"); setPopupSuppressed(false); } else { suppressGradePopups(); } }}
              className="ms-auto rounded border border-black/12 px-2 py-1 text-[10px] font-medium text-[var(--primary)] lift dark:border-white/15">
              {popupSuppressed ? tp("Re-enable conversion tips", lang) : tp("Suppress conversion tips for table", lang)}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-1.5 lg:grid-cols-2">
            {salaryGrades.map((g, i) => {
              const rt = RATE_KEYS.has(g.rate_type) ? g.rate_type : "salary";
              const isSalary = rt === "salary";
              const annual = gnum(g.amount);
              return (
              <div key={g.grade} className="rounded border border-black/10 px-2 py-1 dark:border-white/10" data-testid={`grade-row-${g.grade}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-12 shrink-0 text-xs font-semibold opacity-70">G{g.grade}</span>
                  <input value={g.label || ""} placeholder={tp("label", lang)} maxLength={40}
                    onChange={(e) => editGrade(i, "label", e.target.value)}
                    data-testid={`grade-label-${g.grade}`} className="min-w-0 flex-1 rounded border border-black/10 bg-transparent px-1.5 py-0.5 text-[11px] dark:border-white/15" />
                  <input type="number" step="0.01" min="0" inputMode="decimal" value={Number(g.amount) ? g.amount : ""} placeholder="0"
                    onFocus={(e) => e.target.select()} onBlur={() => flagGradePopup(i)}
                    onChange={(e) => editGrade(i, "amount", e.target.value)}
                    data-testid={`grade-amount-${g.grade}`} className="w-24 shrink-0 rounded border border-black/10 bg-transparent px-1.5 py-0.5 text-[11px] dark:border-white/15" />
                  <select value={rt} onChange={(e) => { editGrade(i, "rate_type", e.target.value); flagGradePopup(i); }}
                    data-testid={`grade-ratetype-${g.grade}`} title={tp("Pay type for this grade", lang)}
                    style={{ color: RATE_COLOR[rt] || "" }}
                    className="w-28 shrink-0 rounded border border-black/10 bg-transparent px-1 py-0.5 text-[10px] font-semibold dark:border-white/15">
                    {RATE_TYPES.map((r) => <option key={r.key} value={r.key} style={{ color: RATE_COLOR[r.key] || "" }}>{tp(r.label, lang)}</option>)}
                  </select>
                  {isSalary && (
                    <select value={g.pay_frequency || "annual"} onChange={(e) => { editGrade(i, "pay_frequency", e.target.value); flagGradePopup(i); }}
                      data-testid={`grade-freq-${g.grade}`} title={tp("How this grade is actually paid", lang)}
                      className="w-24 shrink-0 rounded border border-black/10 bg-transparent px-1 py-0.5 text-[10px] dark:border-white/15">
                      {PAY_FREQ.map((f) => <option key={f.key} value={f.key}>{tp(f.label, lang)}</option>)}
                    </select>
                  )}
                  {isSalary && annual > 0 && gradeView !== "annual" && (
                    <span data-testid={`grade-view-amt-${g.grade}`} className="shrink-0 text-[10px] font-semibold text-[var(--primary)]">
                      = {fmtMoney(annual / (FREQ_DIV[gradeView] || 1))}{FREQ_SHORT[gradeView]}
                    </span>
                  )}
                </div>
                {isSalary && annual > 0 && (
                  <div className="mt-0.5 ps-12 text-[10px] opacity-55" data-testid={`grade-equiv-${g.grade}`}>≈ {gradeEquiv(annual)}</div>
                )}
                {popupRow === i && !popupSuppressed && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 rounded bg-[var(--primary)]/8 px-2 py-1 text-[10px]" data-testid={`grade-popup-${g.grade}`}>
                    <span className="flex-1">
                      {rt === "hourly" ? `${tp("Hourly rate", lang)}: ${fmtMoney(annual)}${tp("/hr", lang)}`
                        : rt === "pay_once" ? `${tp("One-time payment", lang)}: ${fmtMoney(annual)}`
                        : rt === "month_day_1" ? `${fmtMoney(annual)} ${tp("paid monthly on the 1st", lang)}`
                        : `${tp("Annual", lang)} ${fmtMoney(annual)} → ${gradeEquiv(annual)}`}
                    </span>
                    <button type="button" onClick={() => setPopupRow(null)} data-testid={`grade-popup-dismiss-${g.grade}`}
                      className="rounded border border-black/15 px-1.5 py-0.5 font-medium lift dark:border-white/15">{tp("Got it", lang)}</button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-3">
            {gradesMsg && <span className={`text-xs ${gradesMsg.startsWith("Saved") ? "text-[#24A148]" : "text-[#DA1E28]"}`} data-testid="grades-msg">{gradesMsg}</span>}
            {gradesDirty && !gradesMsg && <span className="text-xs text-[#FF832B]" data-testid="grades-unsaved">Unsaved changes</span>}
            <Button variant="ghost" onClick={closeGrades} data-testid="grades-close">{t("close", lang)}</Button>
            <Button onClick={saveGrades} data-testid="grades-save"><Check size={14} /> {tp("Save salary grades", lang)}</Button>
          </div>
        </div>
      </Modal>


      {/* Rename a whole branch everywhere */}
      <Modal open={renamePrompt.open} onClose={() => setRenamePrompt({ open: false, from: "", name: "" })} title={tp("Rename branch everywhere", lang)} testid="rename-branch-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-80">{tp(`Rename the “${renamePrompt.from}” branch on every tagged member in this tree. All members keep their existing colours; only the branch name changes.`, lang)}</p>
          <Input label={tp("New branch name (up to 40 characters)", lang)} maxLength={40} value={renamePrompt.name}
            onChange={(e) => setRenamePrompt((b) => ({ ...b, name: e.target.value }))}
            placeholder={tp("e.g. Paternal line", lang)} data-testid="rename-branch-input" />
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setRenamePrompt({ open: false, from: "", name: "" })} data-testid="rename-branch-cancel">{t("cancel", lang)}</Button>
            <Button onClick={renameBranchAll} disabled={!renamePrompt.name.trim() || renamePrompt.name.trim() === renamePrompt.from} data-testid="rename-branch-apply"><GitBranch size={15} /> {tp("Rename for all members", lang)}</Button>
          </div>
        </div>
      </Modal>
      <Modal open={branchPrompt.open} onClose={() => setBranchPrompt({ open: false, color: "", name: "" })} title={tp("New branch colour", lang)} testid="branch-prompt-modal">
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-block h-6 w-6 rounded-full ring-1 ring-black/10" style={{ background: branchPrompt.color }} data-testid="branch-prompt-swatch" />
            <span className="opacity-80">{tp("You picked a new colour. Create a new branch with it?", lang)}</span>
          </div>
          <Input label={tp("Branch name (up to 40 characters)", lang)} maxLength={40} value={branchPrompt.name}
            onChange={(e) => setBranchPrompt((b) => ({ ...b, name: e.target.value }))}
            placeholder={tp("e.g. Maternal line", lang)} data-testid="branch-name-input" />
          <p className="text-xs opacity-60">{tp("“Yes” starts a new sibling branch (this entry and its descendants use the new name + colour). “Just change colour” keeps the current branch name.", lang)}</p>
          {(() => {
            const br = (form.branch || "").trim();
            const n = people.filter((p) => (activeTree ? p.tree_id === activeTree : true) && (p.branch || "").trim() === br && p.id !== editId).length;
            const total = n + (editId ? 1 : 0);
            return br && n > 0 ? (
              <div className="rounded-md border border-black/10 bg-black/[0.02] p-2.5 dark:border-white/10 dark:bg-white/[0.03]" data-testid="branch-apply-all-box">
                <p className="text-xs opacity-70">{tp(`Or recolour every member tagged to the “${br}” branch (${total} in this tree) in one action, so you don’t have to edit each separately.`, lang)}</p>
                <Button variant="ghost" onClick={applyToBranch} data-testid="branch-apply-all" className="mt-1.5"><GitBranch size={15} /> {tp(`Apply to all “${br}” members (${total})`, lang)}</Button>
              </div>
            ) : null;
          })()}
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setBranchPrompt({ open: false, color: "", name: "" })} data-testid="branch-cancel">{t("cancel", lang)}</Button>
            <Button variant="ghost" onClick={applyColorOnly} data-testid="branch-color-only">{tp("Just change colour", lang)}</Button>
            <Button onClick={applyNewBranch} disabled={!branchPrompt.name.trim()} data-testid="branch-create"><GitBranch size={15} /> {tp("Yes, create branch", lang)}</Button>
          </div>
        </div>
      </Modal>

      {/* Delete tree — record disposition */}
      <Modal open={delModal} onClose={() => setDelModal(false)} title={`Delete "${tree?.name || ""}"`} testid="del-tree-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">This tree holds <b>{activeTree ? inTree.length : 0}</b> record(s). Choose what happens to them before the tree is deleted.</p>
          <Select label={tp("What to do with the records", lang)} value={disp.mode} onChange={(e) => setDisp({ ...disp, mode: e.target.value })} data-testid="disp-mode">
            <option value="reassign" disabled={!otherTrees.length}>Move them to another tree{!otherTrees.length ? " (no other tree)" : ""}</option>
            <option value="delete">{tp("Delete the records permanently", lang)}</option>
            <option value="unassign">Keep them unassigned (moved to the cleanup list)</option>
          </Select>
          {disp.mode === "reassign" && (
            <Select label={tp("Move records to", lang)} value={disp.target_tree_id} onChange={(e) => setDisp({ ...disp, target_tree_id: e.target.value })} data-testid="disp-target">
              <option value="">{tp("— choose a tree —", lang)}</option>
              {otherTrees.map((tr) => <option key={tr.id} value={tr.id}>{`${tr.name} · ${tr.type === "generic" ? "Generic" : "Family"}`}</option>)}
            </Select>
          )}
          {disp.mode === "delete" && <p className="text-xs text-[#DA1E28]">This permanently removes all {inTree.length} record(s) in this tree.</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setDelModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={confirmDelTree} disabled={disp.mode === "reassign" && !disp.target_tree_id} data-testid="confirm-del-tree">{tp("Delete tree", lang)}</Button>
          </div>
        </div>
      </Modal>

      {/* Merge trees */}
      <Modal open={mergeModal} onClose={() => setMergeModal(false)} title={`Merge "${tree?.name || ""}" into another tree`} testid="merge-tree-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">All <b>{activeTree ? inTree.length : 0}</b> record(s) from this tree move into the destination tree, then this tree is deleted.</p>
          <Select label={tp("Merge into", lang)} value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} data-testid="merge-target">
            <option value="">— choose destination tree —</option>
            {otherTrees.map((tr) => <option key={tr.id} value={tr.id}>{`${tr.name} · ${tr.type === "generic" ? "Generic" : "Family"}`}</option>)}
          </Select>
          {tree && mergeTarget && trees.find((x) => x.id === mergeTarget)?.type !== treeType && (
            <p className="text-xs text-[#FF832B]">Note: the destination tree is a different type — merged records keep their data but will display using the destination's field set.</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMergeModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={confirmMerge} disabled={!mergeTarget} data-testid="confirm-merge">{tp("Merge & delete this tree", lang)}</Button>
          </div>
        </div>
      </Modal>

      {/* Print / poster */}
      <Modal open={printModal} onClose={() => setPrintModal(false)} title={`Print "${tree?.name || ""}"`} testid="print-modal">
        <div className="space-y-3">
          {treeType === "generic" && (
            <div className="rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/[0.05] p-2 text-xs opacity-80" data-testid="print-generic-note">
              This organisation prints as a department-grouped table — groups in Group-rank (A–Z) order and people ranked by Salary Grade, matching the on-screen org table.
            </div>
          )}
          {treeType !== "generic" && (
          <Select label={tp("What to print", lang)} value={pOpts.mode} onChange={(e) => setPOpts({ ...pOpts, mode: e.target.value })} data-testid="print-mode">
            <option value="diagram">Diagram (visual tree)</option>
            <option value="cards">Records (cards list)</option>
          </Select>
          )}
          {treeType !== "generic" && pOpts.mode === "diagram" && (
            <>
              <Select label={tp("Layout", lang)} value={pOpts.layout} onChange={(e) => setPOpts({ ...pOpts, layout: e.target.value })} data-testid="print-layout">
                <option value="single">Single sheet (scale to fit one page)</option>
                <option value="panels">Poster panels (tile across sheets for a wall)</option>
              </Select>
              {pOpts.layout === "panels" && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm">Columns
                    <input type="number" min={1} max={6} value={pOpts.cols} onChange={(e) => setPOpts({ ...pOpts, cols: Math.max(1, Math.min(6, Number(e.target.value))) })} data-testid="print-cols"
                      className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5" />
                  </label>
                  <label className="text-sm">Rows
                    <input type="number" min={1} max={6} value={pOpts.rows} onChange={(e) => setPOpts({ ...pOpts, rows: Math.max(1, Math.min(6, Number(e.target.value))) })} data-testid="print-rows"
                      className="mt-1 w-full rounded-md border border-black/15 dark:border-white/15 bg-transparent px-3 py-1.5" />
                  </label>
                  <p className="col-span-2 text-xs opacity-60">Prints {pOpts.cols * pOpts.rows} sheet(s) that tile into a {pOpts.cols}×{pOpts.rows} wall poster.</p>
                </div>
              )}
            </>
          )}
          {treeType === "family" && Array.isArray(printBranches) && (() => {
            const allBr = [...new Set(inTree.map((p) => p.branch || "Main"))].sort();
            const toggleBr = (b) => setPrintBranches((s) => s.includes(b) ? s.filter((x) => x !== b) : [...s, b]);
            const willPrint = inTree.filter((p) => printBranches.includes(p.branch || "Main")).length;
            return (
              <div className="rounded-md border border-black/10 dark:border-white/10 p-2" data-testid="print-branches">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-xs font-semibold opacity-70">Branches to include</span>
                  <div className="ms-auto flex gap-2 text-[11px]">
                    <button onClick={() => setPrintBranches(allBr)} data-testid="print-branches-all" className="text-[var(--primary)] hover:underline">All</button>
                    <button onClick={() => setPrintBranches([])} data-testid="print-branches-none" className="text-[var(--primary)] hover:underline">None</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {allBr.map((b) => {
                    const on = printBranches.includes(b);
                    const count = inTree.filter((p) => (p.branch || "Main") === b).length;
                    return (
                      <button key={b} onClick={() => toggleBr(b)} data-testid={`print-branch-${b}`}
                        className={`rounded border px-2 py-0.5 text-xs transition ${on ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-black/15 opacity-70 hover:opacity-100 dark:border-white/15"}`}>{b} ({count})</button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] opacity-60">{willPrint} of {inTree.length} record(s) will print.</p>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 gap-3">
            <Select label={tp("Paper size", lang)} value={pOpts.paperKey} onChange={(e) => setPOpts({ ...pOpts, paperKey: e.target.value })} data-testid="print-paper">
              {Object.entries(PAPER).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
            <Select label={tp("Orientation", lang)} value={pOpts.orientation} onChange={(e) => setPOpts({ ...pOpts, orientation: e.target.value })} data-testid="print-orientation">
              <option value="portrait">{tp("Portrait", lang)}</option>
              <option value="landscape">{tp("Landscape", lang)}</option>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={pOpts.branding} onChange={(e) => setPOpts({ ...pOpts, branding: e.target.checked })} data-testid="print-branding" />
            Include Familigi™ header &amp; footer (tree name + date)
          </label>
          <p className="text-xs opacity-60">Tip: in the print dialog, set margins to “None” and enable “Background graphics” for best results.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setPrintModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={doPrint} disabled={treeType === "family" && Array.isArray(printBranches) && printBranches.length === 0} data-testid="print-go"><Printer size={15} /> Open print view</Button>
          </div>
        </div>
      </Modal>

      {/* Share a read-only link */}
      <Modal open={shareModal} onClose={() => setShareModal(false)} title={`Share "${tree?.name || ""}"`} testid="share-modal">
        <div className="space-y-4">
          <p className="text-sm opacity-70">Create a public, read-only link relatives can open without an account. They can also suggest people to add — you approve every suggestion.</p>
          {!share.enabled ? (
            <Button onClick={() => saveShare({ enabled: true })} data-testid="share-enable"><Share2 size={16} /> Create share link</Button>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-md border border-black/15 dark:border-white/15 p-2">
                <input readOnly value={shareUrl} data-testid="share-url" className="flex-1 bg-transparent text-sm outline-none" />
                <button onClick={copyLink} data-testid="share-copy" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                  {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Copy"}
                </button>
                <button onClick={emailLink} data-testid="share-email" title="Open a pre-filled email with the link" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                  <Mail size={13} /> Email
                </button>
                <button onClick={whatsappLink} data-testid="share-whatsapp" title="Open WhatsApp with the link ready to send" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                  <MessageCircle size={13} /> WhatsApp
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button onClick={nativeShare} data-testid="share-native" title="Share via your device" className="flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                    <Share2 size={13} /> Share
                  </button>
                )}
              </div>
              <div ref={qrRef} className="flex items-center gap-3 rounded-md border border-black/10 dark:border-white/10 p-3" data-testid="share-qr">
                <QRCodeCanvas value={shareUrl} size={104} includeMargin level="M" bgColor="#ffffff" fgColor="#000000" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium">Scan to open on a phone</p>
                  <p className="text-xs opacity-60">Point a camera at this code to open the read-only tree — no typing, no account.</p>
                  <button onClick={downloadQR} data-testid="share-qr-download" className="mt-1 flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift">
                    <Download size={13} /> Download QR (PNG)
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={share.show_dates} onChange={(e) => saveShare({ show_dates: e.target.checked })} data-testid="share-show-dates" />
                  Show birth &amp; death dates to viewers
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={share.show_photos} onChange={(e) => saveShare({ show_photos: e.target.checked })} data-testid="share-show-photos" />
                  Show photos to viewers
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={share.notify} onChange={(e) => saveShare({ notify: e.target.checked })} data-testid="share-notify" />
                  Notify me when this tree is opened
                </label>
                {share.notify && (
                  <div className="ps-6">
                    <Select value={share.notify_mode} onChange={(e) => saveShare({ notify_mode: e.target.value })} data-testid="share-notify-mode">
                      <option value="first">{tp("On the first open only", lang)}</option>
                      <option value="new_viewer">{tp("Each time a new viewer opens it", lang)}</option>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 border-t border-black/10 pt-3 dark:border-white/10">
                <Button variant="ghost" onClick={regenerateShare} data-testid="share-regenerate"><RefreshCw size={15} /> Regenerate link</Button>
                <Button variant="danger" onClick={revokeShare} data-testid="share-revoke"><X size={15} /> Revoke access</Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-black/[0.03] px-3 py-2 text-xs dark:bg-white/[0.05]" data-testid="share-stats">
                <span className="flex items-center gap-1 opacity-70"><BarChart3 size={13} /> Reach</span>
                <span><b data-testid="share-opens">{share.stats?.opens ?? 0}</b> opens</span>
                <span><b data-testid="share-unique">{share.stats?.unique_visitors ?? 0}</b> unique viewers</span>
                {share.stats?.last_viewed_at
                  ? <span className="opacity-60">last viewed {String(share.stats.last_viewed_at).slice(0, 10)}</span>
                  : <span className="opacity-60">not viewed yet</span>}
              </div>
              <p className="text-xs opacity-50">Regenerating makes the old link stop working. Revoking turns off the link entirely.</p>
            </>
          )}
        </div>
      </Modal>

      {/* Viewer suggestions */}
      <Modal open={suggModal} onClose={() => setSuggModal(false)} title={`Suggestions for "${tree?.name || ""}"`} testid="suggestions-modal" wide>
        <div className="space-y-3">
          {pending.length === 0 && <Card className="opacity-60" data-testid="no-suggestions">{tp("No pending suggestions right now.", lang)}</Card>}
          {pending.map((s) => (
            <Card key={s.id} className="space-y-2" data-testid={`suggestion-${s.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                {s.photo && (
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-black/10 dark:border-white/15">
                    <img src={s.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${s.photo_rot || 0}deg)` }} />
                  </div>
                )}
                <div className="font-head text-lg">{[s.first, s.middle, s.last].filter(Boolean).join(" ") || "(no name given)"}</div>
                {s.branch && <Badge color="info"><GitBranch size={11} className="inline" /> {s.branch}</Badge>}
                {s.parent_name && <Badge>child of {s.parent_name}</Badge>}
                {s.dob && <Badge>b. {s.dob}</Badge>}
                {s.dod && <Badge color="danger">d. {s.dod}</Badge>}
                <span className="ms-auto text-xs opacity-50">from {s.suggester_name} · {(s.created_at || "").slice(0, 10)}</span>
              </div>
              {s.note && <p className="text-sm opacity-80">“{s.note}”</p>}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => dismissSug(s.id)} data-testid={`sug-dismiss-${s.id}`}><X size={15} /> Dismiss</Button>
                <Button onClick={() => acceptSug(s.id)} data-testid={`sug-accept-${s.id}`}><Check size={15} /> Accept &amp; add</Button>
              </div>
            </Card>
          ))}
        </div>
      </Modal>

      {/* Bulk delete confirmation */}
      <Modal open={bulkDelModal} onClose={() => setBulkDelModal(false)} title={tp("Delete selected records", lang)} testid="bulk-del-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Permanently delete <b>{selCount}</b> selected record(s)? This cannot be undone.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setBulkDelModal(false)}>{t("cancel", lang)}</Button>
            <Button variant="danger" onClick={doBulkDelete} data-testid="confirm-bulk-delete"><Trash2 size={15} /> Delete {selCount}</Button>
          </div>
        </div>
      </Modal>

      {/* Create action group */}
      <Modal open={groupModal} onClose={() => setGroupModal(false)} title={tp("Create action group", lang)} testid="group-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Save these <b>{selCount}</b> selected people as a named group — ready to reuse later (e.g. a beneficiary category or a batch to pay).</p>
          <Input label={tp("Group name (up to 88 characters)", lang)} maxLength={88} value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="group-name" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setGroupModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={doCreateGroup} disabled={!groupName.trim()} data-testid="save-group"><FolderPlus size={15} /> Create group</Button>
          </div>
        </div>
      </Modal>

      {/* Move selected to a tree */}
      <Modal open={moveModal} onClose={() => setMoveModal(false)} title={tp("Move selected records", lang)} testid="move-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Move <b>{selCount}</b> selected record(s) into a tree. Choose the current tree to keep them in place, or another tree to move them across.</p>
          <Select label={tp("Destination tree", lang)} value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} data-testid="move-target">
            {trees.map((tr) => <option key={tr.id} value={tr.id}>{`${tr.name} · ${tr.type === "generic" ? "Generic" : "Family"}${tr.id === activeTree ? " (current)" : ""}`}</option>)}
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMoveModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={doMove} disabled={!moveTarget} data-testid="confirm-move"><FolderInput size={15} /> Move {selCount}</Button>
          </div>
        </div>
      </Modal>

      {/* List (export selected to CSV) */}
      <Modal open={listModal} onClose={() => setListModal(false)} title={tp("List selected (export CSV)", lang)} testid="list-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Download a spreadsheet (CSV) of the <b>{selCount}</b> selected record(s). Add an optional title for the file and the sheet.</p>
          <Input label={tp("Title (optional)", lang)} maxLength={120} value={listTitle} onChange={(e) => setListTitle(e.target.value)} placeholder={tp("e.g. Q3 Beneficiaries", lang)} data-testid="list-title" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setListModal(false)}>{t("cancel", lang)}</Button>
            <Button onClick={doExportList} data-testid="confirm-list"><FileSpreadsheet size={15} /> Download CSV</Button>
          </div>
        </div>
      </Modal>

      {/* Saved groups viewer */}
      <Modal open={groupsModal} onClose={() => setGroupsModal(false)} title={tp("Action groups", lang)} testid="groups-modal" wide>
        <div className="space-y-3">
          {groups.some((g) => g.criteria?.type === "band") && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-black/10 bg-black/[0.02] px-3 py-2 dark:border-white/10 dark:bg-white/[0.03]">
              <span className="text-xs opacity-70">{resyncAllMsg || tp("Band groups update as people are added/aged/deceased.", lang)}</span>
              <Button variant="ghost" onClick={resyncAllGroups} data-testid="resync-all-groups"><RefreshCw size={14} /> {tp("Re-sync all band groups", lang)}</Button>
            </div>
          )}
          {groups.length === 0 && <Card className="opacity-60" data-testid="no-groups">No saved groups yet. Select people, then “Create group”.</Card>}
          {groups.map((g) => {
            const open = !!expandedGroups[g.id];
            const ids = g.member_ids || [];
            return (
              <Card key={g.id} className="space-y-2" data-testid={`group-${g.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => setExpandedGroups((s) => ({ ...s, [g.id]: !s[g.id] }))} data-testid={`group-toggle-${g.id}`} className="flex items-center gap-2 text-start">
                    {open ? <ChevronDown size={16} className="text-[var(--primary)]" /> : <ChevronRight size={16} className="text-[var(--primary)]" />}
                    <Users size={16} className="text-[var(--primary)]" />
                    <span className="font-head text-base">{g.name}</span>
                  </button>
                  <Badge>{g.member_count ?? ids.length} member(s)</Badge>
                  {g.tree_id && trees.find((x) => x.id === g.tree_id) && <span className="text-xs opacity-50">{trees.find((x) => x.id === g.tree_id)?.name}</span>}
                  {g.criteria?.type === "band" && (
                    <Button variant="ghost" onClick={() => resyncGroup(g)} data-testid={`group-resync-${g.id}`} title={tp("Recompute membership from current data", lang)}><RefreshCw size={14} /> {tp("Re-sync", lang)}</Button>
                  )}
                  <span className="ms-auto text-xs opacity-50">{(g.updated_at || g.created_at || "").slice(0, 10)}</span>
                  <button onClick={() => deleteGroup(g.id)} data-testid={`del-group-${g.id}`} className="opacity-60 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={15} /></button>
                </div>
                {open && (
                  <div className="rounded-md border border-black/5 bg-black/[0.02] p-2 text-sm dark:border-white/5 dark:bg-white/[0.03]" data-testid={`group-members-${g.id}`}>
                    {ids.length === 0 ? <span className="opacity-60">{tp("No members.", lang)}</span> : (
                      <div className="flex flex-wrap gap-1.5">
                        {ids.map((id) => <span key={id} className="rounded bg-black/5 px-1.5 py-0.5 text-xs dark:bg-white/10">{nameById(id) || tp("(not in this tree)", lang)}</span>)}
                      </div>
                    )}
                    {g.criteria?.type === "band" && <p className="mt-1.5 text-[11px] opacity-55">{tp("Auto-built from Demographics", lang)}: {g.criteria.branch === "__ALL__" ? tp("All branches", lang) : g.criteria.branch} · {g.criteria.min}–{g.criteria.max}{g.criteria.living_only ? ` · ${tp("living", lang)}` : ""}. {tp("Use Re-sync to refresh as birthdays/deaths change.", lang)}</p>}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </Modal>

      {/* New tree */}
      <Modal open={treeModal} onClose={() => setTreeModal(false)} title={tp("New tree", lang)} testid="tree-modal">
        <div className="space-y-3">
          <Input label={tp("Tree name (up to 88 characters)", lang)} maxLength={88} value={newTree.name} onChange={(e) => setNewTree({ ...newTree, name: e.target.value })} data-testid="tree-name" />
          <Select label={tp("Type", lang)} value={newTree.type} onChange={(e) => setNewTree({ ...newTree, type: e.target.value })} data-testid="tree-type">
            <option value="family">{tp("Family option", lang)}</option>
            <option value="generic">{tp("Generic option", lang)}</option>
          </Select>
          <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setTreeModal(false)}>{t("cancel", lang)}</Button><Button onClick={createTree} data-testid="save-tree">{t("save", lang)}</Button></div>
        </div>
      </Modal>

      {/* Field labels */}
      <Modal open={labelModal} onClose={() => setLabelModal(false)} title={tp("Customize field labels", lang)} testid="label-modal">
        <div className="space-y-3">
          <p className="text-xs opacity-60">Rename this tree's fields. Leave blank to use the default. Blank fields let you define your own.</p>
          <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-auto pe-1">
            {fields.map((f) => (
              <Input key={f.key} label={f.label || `Custom (${f.key})`} value={labelDraft[f.key] || ""} placeholder={f.label || "Custom field"}
                onChange={(e) => setLabelDraft({ ...labelDraft, [f.key]: e.target.value })} data-testid={`label-${f.key}`} />
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setLabelModal(false)}>{t("cancel", lang)}</Button><Button onClick={saveLabels} data-testid="save-labels">{t("save", lang)}</Button></div>
        </div>
      </Modal>
      <Modal open={payrollOpen} onClose={() => setPayrollOpen(false)} title={tp("Export processed rate cards", lang)} testid="payroll-csv-modal">
        <div className="space-y-3">
          <p className="text-xs opacity-60">{tp("Downloads a CSV of every rate card marked 'Processed'. Optionally limit to a pay period.", lang)}</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-0.5 block text-[11px] font-medium opacity-70">{tp("Period start", lang)}</label>
              <input type="date" value={payrollStart} onChange={(e) => setPayrollStart(e.target.value)} data-testid="payroll-start" className="w-full rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15" /></div>
            <div><label className="mb-0.5 block text-[11px] font-medium opacity-70">{tp("Period end", lang)}</label>
              <input type="date" value={payrollEnd} onChange={(e) => setPayrollEnd(e.target.value)} data-testid="payroll-end" className="w-full rounded border border-black/15 bg-transparent px-2 py-1 text-sm dark:border-white/15" /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPayrollOpen(false)}>{t("cancel", lang)}</Button>
            <Button onClick={exportPayrollCsv} disabled={payrollBusy} data-testid="payroll-export"><FileSpreadsheet size={15} /> {tp("Export CSV", lang)}</Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
